<template>
  <div class="dt-wrap">
    <!-- ===================== LIST VIEW ===================== -->
    <template>
      <div class="dt-header">
        <div>
          <h1 class="dt-title">Onboarding Tracker</h1>
          <p class="dt-sub">All agents first, then 40 vendors, then 100 homeowners.</p>
        </div>
        <div class="dt-header-actions">
          <button class="dt-btn dt-btn-ghost dt-daily-btn" @click="openMessaging">
            📨 Messaging
            <span v-if="summary && summary.total_unread > 0" class="dt-daily-badge">{{ summary.total_unread }}</span>
          </button>
          <button class="dt-btn dt-btn-ghost" @click="openInsights">📊 Insights</button>
          <button class="dt-btn dt-btn-ghost" @click="openHistory">🕘 History</button>
          <button class="dt-btn dt-btn-ghost" @click="openAiActivity">🤖 AI Activity</button>
          <button class="dt-btn dt-btn-ghost dt-daily-btn" @click="dailyModal = true">
            📋 Daily tasks
            <span v-if="dueCount > 0" class="dt-daily-badge">{{ dueCount }}</span>
          </button>
          <button class="dt-btn dt-btn-ghost" :disabled="loading" @click="manualRefresh">
            <span v-if="loading">Refreshing…</span><span v-else>Refresh</span>
          </button>
          <button class="dt-btn dt-btn-primary" @click="openCreate">+ Add Team</button>
          <button class="dt-btn dt-btn-ghost dt-settings-btn" title="Outreach settings" @click="openSettings">⚙ Settings</button>
        </div>
      </div>

      <div v-if="errorMsg" class="dt-error">{{ errorMsg }}</div>

      <div v-if="summary" class="dt-pipeline">
        <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="openSteps">✎ Edit action steps</button>
        <span class="dt-pipeline-stat">{{ summary.total_open_alerts }} open actions · {{ summary.teams_with_alerts }} teams</span>
      </div>

      <!-- ===== ONE table: everything inline, color-coded, bucketed by Due ===== -->
      <section class="dt-card dt-teams-card">
        <div class="dt-teams-head">
          <h2 class="dt-section-title">All teams <span class="dt-count">{{ sortedTeams.length }}</span></h2>
          <input v-model="teamSearch" class="dt-input dt-team-search" type="search" placeholder="🔍 Search teams…" />
          <select v-model="assignedFilter" class="dt-input dt-assignee-filter" title="Filter by who owns the account">
            <option value="">All owners</option>
            <option value="__unassigned">Unassigned</option>
            <option v-for="name in assignees" :key="name" :value="name">{{ name }}</option>
          </select>
        </div>
        <div class="dt-table-scroll">
          <table class="dt-table dt-table-wide">
            <colgroup>
              <col style="width: 26px;" />
              <col style="width: 100px;" />
              <col style="width: 15%;" />
              <col style="width: 17%;" />
              <col style="width: 28%;" />
              <col style="width: 18%;" />
              <col style="width: 9%;" />
              <col style="width: 34px;" />
            </colgroup>
            <thead>
              <tr>
                <th><span class="dt-th"></span></th>
                <th class="sortable" :class="{ active: sortKey === 'due' }" @click="setSort('due')"><span class="dt-th">Due {{ sortIndicator('due') }}</span></th>
                <th class="sortable" :class="{ active: sortKey === 'team_name' }" @click="setSort('team_name')"><span class="dt-th">Team {{ sortIndicator('team_name') }}</span></th>
                <th class="dt-next-cell"><span class="dt-th">Next action</span></th>
                <th class="dt-notes-col dt-note-th"><span class="dt-th">Latest note</span></th>
                <th class="sortable" :class="{ active: sortKey === 'followup' }" @click="setSort('followup')"><span class="dt-th">Task {{ sortIndicator('followup') }}</span></th>
                <th class="sortable" :class="{ active: sortKey === 'completion' }" @click="setSort('completion')"><span class="dt-th">Progress {{ sortIndicator('completion') }}</span></th>
                <th><span class="dt-th"></span></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(t, i) in sortedTeams">
              <tr v-if="showSepBefore(i)" :key="'sep-' + t.id" class="dt-bucket-sep">
                <td colspan="9"><span class="dt-bucket-sep-label">{{ dueBucket(t) }}</span></td>
              </tr>
              <tr
                :key="t.id"
                class="dt-row"
                :class="{ 'row-dragover': rowDragOverIndex === i, 'dt-done-today': doneToday(t) }"
                :style="{ boxShadow: 'inset 5px 0 0 ' + teamColor(t.id) }"
                :draggable="rowDragArmed"
                @dragstart="onRowDragStart(i)"
                @dragover.prevent="onRowDragOver(i)"
                @drop="onRowDrop(i)"
                @dragend="rowDragArmed = false; rowDragOverIndex = null"
              >
                <td class="dt-rowdrag-cell">
                  <span class="dt-drag" :class="{ 'dt-drag-off': teamSearch && teamSearch.trim() }" :title="teamSearch && teamSearch.trim() ? 'Clear search to reorder' : 'Drag to reorder'" @mousedown="rowDragArmed = !(teamSearch && teamSearch.trim())" @mouseup="rowDragArmed = false">⠿</span>
                </td>
                <td>
                  <span class="dt-bucket-chip" :class="'bk-' + bucketSlug(dueBucket(t))">{{ dueBucket(t) }}</span>
                  <button v-if="doneToday(t)" type="button" class="dt-done-chip dt-chip-btn" title="Click to move back to In progress" @click.stop="setRowStatus(t, 'in_progress')">✓ Done today</button>
                  <button v-else type="button" class="dt-progress-chip dt-chip-btn" title="Click to mark Done for today" @click.stop="setRowStatus(t, 'done')">● In progress</button>
                </td>
                <td class="dt-clickable" @click="openTeam(t)">
                  <div class="dt-team-name">
                    <span class="dt-team-dot" :style="{ background: teamColor(t.id) }"></span>
                    <span class="dt-team-name-text" :title="t.team_name">{{ t.team_name }}</span>
                    <span v-if="t.unread_comms > 0" class="dt-envelope" :title="t.unread_comms + ' new repl' + (t.unread_comms === 1 ? 'y' : 'ies')">✉ {{ t.unread_comms }}</span>
                  </div>
                  <div class="dt-muted dt-team-reg">Registered: {{ t.registered_date ? fmtDate(t.registered_date) : '—' }}<span v-if="t.counts_source === 'auto'" class="dt-auto"> · ⟳ auto</span><span v-if="t.assigned_to" class="dt-assignee"> · 👤 {{ t.assigned_to }}</span></div>
                  <div class="dt-muted dt-team-reg">Last action: {{ t.last_action || '—' }}<span v-if="t.last_action_at"> · {{ fmtDate(t.last_action_at) }}</span></div>
                </td>
                <td class="dt-next-cell">
                  <template v-if="shownStep(t)">
                    <div class="dt-next-row">
                      <button class="dt-step-arrow" :disabled="shownStepIdx(t) <= 0" title="Previous step" @click.stop="rowNav(t, -1)">◀</button>
                      <button
                        class="dt-step-chip dt-step-chip-colored"
                        :class="{ pending: shownStep(t).state === 'pending' }"
                        :style="stepChipStyle(shownStep(t))"
                        :title="shownStep(t).label"
                        @click.stop="openTask({ team_id: t.id, team_name: t.team_name }, shownStep(t))"
                      >
                        <span class="dt-chip-label">{{ shownStep(t).label }}</span>
                        <span v-if="shownStep(t).complete" class="dt-chip-done">✓</span>
                        <span v-else-if="shownStep(t).state === 'pending'" class="dt-chip-pending">pending</span>
                      </button>
                      <button class="dt-step-arrow" :disabled="shownStepIdx(t) >= stepList(t).length - 1" title="Next step" @click.stop="rowNav(t, 1)">▶</button>
                    </div>
                    <div class="dt-step-meta" :class="t.days_in_step > 7 ? 'dt-due-red' : 'dt-muted'">Step {{ stepBadge(shownStep(t)) }} · {{ stepPct(shownStep(t)) }}% · {{ t.days_in_step }}d in step</div>
                  </template>
                  <span v-else class="dt-yes">✓ done</span>
                </td>
                <td class="dt-note-cell dt-note-td dt-clickable" :title="t.last_note" @click="openField(t, 'note')">
                  <span v-if="t.last_note" class="dt-note-text">{{ t.last_note }}</span>
                  <span v-else class="dt-muted">+ note</span>
                </td>
                <td class="dt-clickable" @click="openField(t, 'followup')">
                  <template v-if="t.next_followup_at || t.open_followups > 0">
                    <div class="dt-fu-cell-title" :title="teamNextFuTitle(t)">📌 {{ teamNextFuTitle(t) }}</div>
                    <div class="dt-fu-cell-date" :class="{ 'dt-due-red': t.next_followup_at && t.next_followup_at < endOfToday() }">
                      {{ t.next_followup_at ? fmtDate(t.next_followup_at) : 'no date' }}
                    </div>
                  </template>
                  <span v-else class="dt-muted">+ follow-up</span>
                </td>
                <td class="dt-clickable dt-avh" @click="openField(t, 'counts')">
                  <div><b>A</b> {{ t.agents_active }}/{{ t.agents_target || t.agents_total || '?' }} <span class="dt-muted">{{ pct(t.agents_active, t.agents_target || t.agents_total) }}%</span></div>
                  <div><b>V</b> {{ t.vendors_active }}/40 <span class="dt-muted">{{ pct(t.vendors_active, 40) }}%</span></div>
                  <div><b>H</b> {{ t.homeowners_active }}/100 <span class="dt-muted">{{ pct(t.homeowners_active, 100) }}%</span></div>
                  <div class="dt-avh-overall">
                    <span>Overall {{ t.progress.overall }}%</span>
                    <span class="dt-bar dt-bar-thin dt-overall-bar"><span class="dt-bar-fill overall" :style="{ width: t.progress.overall + '%' }"></span></span>
                  </div>
                </td>
                <td><button class="dt-row-delete" title="Delete this team" @click.stop="deleteRow(t)">✕</button></td>
              </tr>
              </template>
              <tr v-if="teams.length === 0 && !loading">
                <td colspan="9" class="dt-empty">No teams yet. Click “Add Team” to start tracking one.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- ===================== TEAM DETAIL (modal) ===================== -->
    <div v-if="active" class="dt-overlay dt-profile-overlay">
      <div class="dt-modal dt-modal-profile">
        <div class="dt-profile-bar">
          <div class="dt-profile-actions">
            <span class="dt-save-state" :class="saveState">{{ saveStateLabel }}</span>
            <button class="dt-btn dt-btn-ghost" @click="openOutreach(active.id, active.team_name)">✉ Draft outreach</button>
            <button class="dt-btn dt-btn-danger-ghost" @click="confirmDelete">Delete</button>
          </div>
          <button class="dt-back" @click="askCloseStatus">✕ Close</button>
        </div>

        <div class="dt-profile-title">
          <span class="dt-team-dot dt-team-dot-lg" :style="{ background: teamColor(active.id) }"></span>
          <input v-model="form.team_name" class="dt-name-input" placeholder="Team name" />
          <span class="dt-stage-pill" :class="stageClass(active.stage)">{{ active.stage }}</span>
          <span class="dt-status-chip" :class="'st-' + (form.status || 'Prospect').toLowerCase()">{{ form.status }}</span>
        </div>

        <div class="dt-profile-body">
          <!-- Full message history (texts + emails, both directions) + compose -->
          <section class="dt-card">
            <h3 class="dt-card-title">Message history <span v-if="activeComms.length" class="dt-count">{{ activeComms.length }}</span>
              <button class="dt-btn dt-btn-ghost dt-btn-sm dt-hist-inline" title="Full action history for this account" @click="openHistory(active.id)">🕘 Action history</button>
            </h3>

            <!-- Compose: email or SMS to any contact (grouped by permission) or custom -->
            <div class="dt-compose">
              <div class="dt-channel-btns">
                <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: profileChannel === 'email' }" @click="profileChannel = 'email'">✉ Email</button>
                <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: profileChannel === 'sms' }" @click="profileChannel = 'sms'">📱 SMS</button>
                <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="profileDrafting" @click="profileDraft">{{ profileDrafting ? 'drafting…' : '✨ Draft with AI' }}</button>
              </div>
              <div class="dt-send-row">
                <contact-picker :contacts="activeContacts" :field="profileChannel === 'sms' ? 'phone' : 'email'" :value="profileTo" :placeholder="profileChannel === 'sms' ? 'search a contact or type any phone' : 'search a contact or type any email'" @input="profileTo = $event" />
              </div>
              <div v-if="profileTo && !recipientValid(profileTo, profileChannel)" class="dt-input-err">Enter a valid {{ profileChannel === 'sms' ? 'phone number' : 'email address' }}.</div>
              <select v-if="templatesFor(profileChannel, 'profile').length" class="dt-input dt-tpl-pick" @change="applyTemplate($event.target.value, 'profile'); $event.target.value = ''">
                <option value="">📋 Insert template…</option>
                <option v-for="t in templatesFor(profileChannel, 'profile')" :key="t.id" :value="t.id">{{ t.name }}</option>
              </select>
              <input v-if="profileChannel === 'email'" v-model="profileSubject" class="dt-input dt-compose-subject" placeholder="Subject" />
              <textarea ref="profileBodyEl" v-model="profileBody" class="dt-input dt-compose-body" rows="4" :placeholder="profileChannel === 'sms' ? 'Text message…' : 'Email message…'" @input="autoGrow($event.target)"></textarea>
              <div v-if="profileChannel === 'email' && outreach.signature" class="dt-sig-preview"><span class="dt-sig-preview-label">Signature (added automatically)</span><br>{{ outreach.signature }}</div>
              <div v-if="profileChannel === 'sms' && smsWillAppendStop(profileTo, activeContacts)" class="dt-sig-preview"><span class="dt-sig-preview-label">Compliance</span><br>"Reply STOP to opt out" is auto-added only on the first text to a non-Kiey number — existing Kiey users are skipped.</div>
              <div class="dt-foot-row">
                <button v-if="profileChannel === 'email' && outreach.attachments.length" type="button" class="dt-attach-btn" :class="{ active: profileAttachUrls.length }" title="Attach files" @click="openAttach('profile')">📎<span v-if="profileAttachUrls.length" class="dt-attach-count">{{ profileAttachUrls.length }}</span></button>
                <span class="dt-foot-spacer"></span>
                <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="profileSending || !recipientValid(profileTo, profileChannel) || !profileBody.trim()" @click="profileSend">{{ profileSending ? 'sending…' : ('Send ' + (profileChannel === 'sms' ? 'SMS' : 'email')) }}</button>
              </div>
            </div>

            <p v-if="activeComms.length === 0" class="dt-empty">No messages yet — emails and texts sent or received will appear here.</p>
            <ul v-else class="dt-comms-list dt-comms-list-tall">
              <li v-for="c in activeComms" :key="c.id" class="dt-comm dt-comm-click" :class="'dir-' + c.direction" @click="openCommView(c, activeComms)">
                <span class="dt-comm-icon">{{ c.channel === 'sms' ? '📱' : '📧' }}</span>
                <div class="dt-comm-body">
                  <div class="dt-comm-head">
                    <span class="dt-dir-badge" :class="'dir-' + c.direction">{{ c.direction === 'out' ? 'Sent' : 'Received' }}</span>
                    <span class="dt-comm-subject-line">{{ c.subject || (c.body || '').slice(0, 70) }}</span>
                    <span v-if="c.channel === 'email' && c.direction === 'out'" class="dt-read-chip" :class="readClass(c)">{{ readLabel(c) }}</span>
                    <span class="dt-comm-caret">›</span>
                  </div>
                  <div class="dt-comm-meta">
                    {{ c.direction === 'out' ? 'Kiey Onboarding → ' + c.to : c.from + ' → Kiey Onboarding' }}
                    · {{ fmtDateTime(c.created_at) }}{{ c.status ? ' · ' + c.status : '' }}
                  </div>
                </div>
              </li>
            </ul>
          </section>

          <!-- Notes -->
          <section class="dt-card">
            <h3 class="dt-card-title">Notes</h3>
            <div class="dt-add-update">
              <input
                ref="updateInput"
                v-model="newUpdate"
                class="dt-input"
                placeholder="Add a note… (saved with a timestamp)"
                @keyup.enter="postUpdate"
              />
              <button class="dt-btn dt-btn-primary" :disabled="!newUpdate.trim() || postingUpdate" @click="postUpdate">Add note</button>
            </div>

            <!-- Prompt to set a follow-up task after a note is added -->
            <div v-if="followupPrompt" class="dt-followup-prompt">
              <div class="dt-flabel">Add a follow-up task?</div>
              <div class="dt-add-update">
                <input v-model="followupTitle" class="dt-input" placeholder="e.g. Call back about the vendor list" @keyup.enter="saveFollowup" />
                <input v-model="followupDate" type="date" class="dt-input dt-fu-date" />
                <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="!followupTitle.trim()" @click="saveFollowup">Add task</button>
                <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="followupPrompt = false">Skip</button>
              </div>
            </div>

            <!-- This team's open follow-up tasks -->
            <ul v-if="teamFollowups.length" class="dt-team-tasks">
              <li v-for="f in teamFollowups" :key="f.id" class="dt-team-task" :class="{ overdue: isOverdue(f) }">
                <input type="checkbox" @change="completeFu(f.id)" />
                <span class="dt-team-task-title">{{ f.title }}</span>
                <span v-if="f.due_date" class="dt-muted">· due {{ fmtDate(f.due_date) }}</span>
              </li>
            </ul>

            <ul class="dt-updates">
              <li v-for="u in updates" :key="u.id" class="dt-update">
                <div class="dt-update-meta">
                  <b>{{ u.author_name || 'Super User' }}</b>
                  <span class="dt-muted">{{ fmtDateTime(u.created_at) }}</span>
                  <span v-if="u.edited_at" class="dt-muted">· edited</span>
                  <button v-if="editingNoteId !== u.id" class="dt-link-btn" @click="startEditNote(u)">edit</button>
                  <button v-if="editingNoteId !== u.id" class="dt-link-btn dt-link-danger" @click="removeNote(u)">delete</button>
                </div>
                <div v-if="editingNoteId === u.id" class="dt-add-update">
                  <input v-model="editNoteText" class="dt-input" @keyup.enter="saveEditNote(u)" />
                  <button class="dt-btn dt-btn-primary dt-btn-sm" @click="saveEditNote(u)">Save</button>
                  <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="editingNoteId = null">Cancel</button>
                </div>
                <div v-else class="dt-update-text">{{ u.text }}</div>
              </li>
              <li v-if="updates.length === 0" class="dt-muted dt-empty-sm">No notes yet.</li>
            </ul>
          </section>

          <!-- Funnel -->
          <section class="dt-card">
            <h3 class="dt-card-title">Funnel progress · {{ active.progress.overall }}% complete</h3>
            <div class="dt-funnel">
              <div class="dt-leg">
                <div class="dt-leg-head"><span>Agents (all)</span><b>{{ form.agents_active }}/{{ form.agents_target || '?' }}</b></div>
                <div class="dt-bar"><div class="dt-bar-fill agents" :style="{ width: legPct('agents') + '%' }"></div></div>
              </div>
              <div class="dt-leg">
                <div class="dt-leg-head"><span>Vendors (min 12 · complete 40)</span><b>{{ form.vendors_active }}/40</b></div>
                <div class="dt-bar dt-bar-marked">
                  <div class="dt-bar-fill vendors" :style="{ width: legPct('vendors') + '%' }"></div>
                  <span class="dt-marker" :style="{ left: (12/40*100) + '%' }" title="Minimum 12"></span>
                </div>
              </div>
              <div class="dt-leg">
                <div class="dt-leg-head"><span>Homeowners (complete 100)</span><b>{{ form.homeowners_active }}/100</b></div>
                <div class="dt-bar"><div class="dt-bar-fill homeowners" :style="{ width: legPct('homeowners') + '%' }"></div></div>
              </div>
            </div>
          </section>

          <!-- Triggers -->
          <section v-if="active.alerts && active.alerts.length" class="dt-card">
            <h3 class="dt-card-title">Triggers &amp; next actions</h3>
            <div v-for="(a, i) in active.alerts" :key="i" class="dt-alert" :class="'sev-' + a.severity">
              <div class="dt-alert-row">
                <div class="dt-alert-main">
                  <div class="dt-alert-title"><span class="dt-sev-dot" :class="'sev-' + a.severity"></span>{{ a.title }}</div>
                  <div v-if="a.detail" class="dt-alert-detail">{{ a.detail }}</div>
                </div>
                <button
                  v-if="alertAction(a)"
                  class="dt-btn dt-btn-ghost dt-btn-sm"
                  :disabled="resolvingKey === a.key"
                  @click="resolveAlert(a)"
                >
                  {{ resolvingKey === a.key ? '…' : alertAction(a).label }}
                </button>
              </div>
              <div v-if="a.script" class="dt-script">📋 {{ a.script }}</div>
            </div>
          </section>

          <!-- Live counts -->
          <section class="dt-card">
            <h3 class="dt-card-title">
              Live counts
              <span v-if="form.counts_source === 'auto'" class="dt-auto">⟳ auto-synced</span>
              <span v-else class="dt-manual">manual</span>
            </h3>
            <div class="dt-link-row">
              <input v-model="tenantQuery" class="dt-input" placeholder="Search tenant to link…" @input="onTenantSearch" />
              <button class="dt-btn dt-btn-ghost" :disabled="!form.tenant_id || syncing" @click="doSync">
                {{ syncing ? 'Syncing…' : 'Sync from tenant' }}
              </button>
            </div>
            <ul v-if="tenantResults.length" class="dt-tenant-results">
              <li v-for="tr in tenantResults" :key="tr.tenant_id" @click="linkTenant(tr)">
                {{ tr.name }} <span class="dt-muted">· {{ tr.tenant_id }}</span>
              </li>
            </ul>
            <div v-if="form.tenant_id" class="dt-linked">
              Linked tenant: <code>{{ form.tenant_id }}</code>
              <button class="dt-link-clear" @click="form.tenant_id = null">unlink</button>
            </div>
            <div class="dt-fields-grid">
              <div class="dt-field"><span class="dt-flabel">Agents active</span><input v-model.number="form.agents_active" type="number" min="0" /></div>
              <div class="dt-field"><span class="dt-flabel">Agent roster (total)</span><input v-model.number="form.agents_target" type="number" min="0" /></div>
              <div class="dt-field"><span class="dt-flabel">Vendors active</span><input v-model.number="form.vendors_active" type="number" min="0" /></div>
              <div class="dt-field"><span class="dt-flabel">Vendors total</span><input v-model.number="form.vendors_total" type="number" min="0" /></div>
              <div class="dt-field"><span class="dt-flabel">Homeowners active</span><input v-model.number="form.homeowners_active" type="number" min="0" /></div>
              <div class="dt-field"><span class="dt-flabel">Homeowners total</span><input v-model.number="form.homeowners_total" type="number" min="0" /></div>
            </div>
          </section>

          <!-- Details -->
          <section class="dt-card">
            <h3 class="dt-card-title">Team details</h3>
            <div class="dt-fields-grid">
              <div class="dt-field">
                <span class="dt-flabel">Status</span>
                <select v-model="form.status"><option>Prospect</option><option>Free</option><option>Paying</option></select>
              </div>
              <div class="dt-field"><span class="dt-flabel">Registered</span><input v-model="form.registered_date" type="date" /></div>
              <div class="dt-field"><span class="dt-flabel">Assigned to</span><input v-model="form.assigned_to" type="text" placeholder="onboarding owner" /></div>
              <div class="dt-field"><span class="dt-flabel">Admin name</span><input v-model="form.admin_name" type="text" /></div>
              <div class="dt-field"><span class="dt-flabel">Admin email</span><input v-model="form.admin_email" type="email" /></div>
              <div class="dt-field"><span class="dt-flabel">Admin phone</span><input v-model="form.admin_phone" type="text" /></div>
              <div class="dt-field dt-field-check">
                <label class="dt-check-label"><input v-model="form.intro_demo_done" type="checkbox" /> Intro demo done</label>
              </div>
              <div class="dt-field"><span class="dt-flabel">Intro demo date</span><input v-model="form.intro_demo_date" type="date" /></div>
              <div class="dt-field"><span class="dt-flabel">Next meeting</span><input v-model="form.next_meeting_date" type="date" /></div>
              <div class="dt-field dt-span2"><span class="dt-flabel">Next meeting note</span><input v-model="form.next_meeting_note" type="text" /></div>
              <div class="dt-field dt-span2"><span class="dt-flabel">Marketing packages</span><input v-model="form.marketing_packages" type="text" /></div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Guided task modal -->
    <div v-if="taskModal && taskStep" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="closeTask">✕</button>
        <h3 class="dt-card-title">{{ taskTeamName }} — {{ taskStep.label }}</h3>
        <div v-if="taskAllSteps.length > 1" class="dt-step-nav">
          <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="taskStepIndex <= 0" @click="gotoStep(taskStepIndex - 1)">◀ Prev step</button>
          <span class="dt-step-count">Step {{ taskStepIndex + 1 }} of {{ taskAllSteps.length }}<span v-if="taskStep.complete" class="dt-step-done-tag">✓ done</span></span>
          <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="taskStepIndex >= taskAllSteps.length - 1" @click="gotoStep(taskStepIndex + 1)">Next step ▶</button>
        </div>
        <p class="dt-task-desc">{{ taskStep.description }}</p>

        <div v-if="taskStep.sub_steps && taskStep.sub_steps.length" class="dt-substeps">
          <div class="dt-flabel dt-substeps-head">
            <span>Steps to complete</span>
            <span class="dt-step-pct">{{ (taskStep.sub_done || []).length }}/{{ taskStep.sub_steps.length }} · {{ stepPct(taskStep) }}%</span>
          </div>
          <div class="dt-step-progress"><span class="dt-step-progress-fill" :style="{ width: stepPct(taskStep) + '%' }"></span></div>
          <label v-for="(ss, j) in taskStep.sub_steps" :key="j" class="dt-substep-check">
            <input type="checkbox" :checked="(taskStep.sub_done || []).includes(j)" @change="toggleSub(j)" />
            <span class="dt-sub-letter">{{ String.fromCharCode(65 + j) }}.</span>
            <span :class="{ 'dt-sub-done': (taskStep.sub_done || []).includes(j) }">{{ ss }}</span>
          </label>
        </div>

        <div v-if="taskStep.state === 'pending'" class="dt-pending-banner">
          <span>⏳ Pending — your part is done; waiting on the live count to confirm.</span>
          <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="taskBusy" @click="completeStep">Mark verified complete</button>
        </div>

        <div class="dt-task-section">
          <div class="dt-flabel">Draft outreach with AI to finish this step</div>
          <div class="dt-channel-btns">
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: taskDraftChannel === 'email' }" @click="draftChannel('email')">Email</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: taskDraftChannel === 'sms' }" @click="draftChannel('sms')">SMS</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: taskDraftChannel === 'phone' }" @click="draftChannel('phone')">Phone script</button>
          </div>
          <p v-if="taskDraftLoading" class="dt-empty">Drafting with AI…</p>
          <div v-else-if="taskDraftText" class="dt-field">
            <span class="dt-flabel">
              {{ taskDraftLabel }}
              <button class="dt-copy" @click="copyText(taskDraftText)">copy</button>
            </span>
            <div v-if="taskDraftChannel === 'email' && taskDraft" class="dt-subject-row">
              <span class="dt-subject-tag">Subject</span>
              <input v-model="taskDraft.subject" class="dt-recipient" placeholder="Email subject" />
            </div>
            <div v-if="taskDraftChannel === 'email'" class="dt-send-row">
              <contact-picker :contacts="taskFilteredContacts" field="email" :value="taskEmailTo" placeholder="search a contact or type any email" @input="taskEmailTo = $event" />
              <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="sending || !taskEmailTo" @click="sendEmail(taskTeamId, taskDraft.subject, taskDraft.email, taskEmailTo)">{{ sending ? 'sending…' : '✉ send' }}</button>
            </div>
            <div v-else-if="taskDraftChannel === 'sms'" class="dt-send-row">
              <contact-picker :contacts="taskFilteredContacts" field="phone" :value="taskSmsTo" placeholder="search a contact or type any phone" @input="taskSmsTo = $event" />
              <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="smsSending || !taskSmsTo" @click="sendSmsTo(taskTeamId, taskDraft.sms, taskSmsTo)">{{ smsSending ? 'sending…' : '📱 send' }}</button>
            </div>
            <textarea v-model="taskDraftText" rows="7" placeholder="Edit the draft here before sending…"></textarea>
          </div>
        </div>

        <div v-if="taskStepComms.length" class="dt-task-section dt-comms-history">
          <div class="dt-flabel">Messages for this step</div>
          <ul class="dt-comms-list">
            <li v-for="c in taskStepComms" :key="c.id" class="dt-comm dt-comm-click" :class="'dir-' + c.direction" @click="openCommView(c, taskStepComms)">
              <span class="dt-comm-icon">{{ c.channel === 'sms' ? '📱' : '📧' }}</span>
              <div class="dt-comm-body">
                <div class="dt-comm-head">
                  <span class="dt-dir-badge" :class="'dir-' + c.direction">{{ c.direction === 'out' ? 'Sent' : 'Received' }}</span>
                  <span class="dt-comm-subject-line">{{ c.subject || (c.body || '').slice(0, 60) }}</span>
                  <span v-if="c.channel === 'email' && c.direction === 'out'" class="dt-read-chip" :class="readClass(c)">{{ readLabel(c) }}</span>
                  <span class="dt-comm-caret">›</span>
                </div>
                <div class="dt-comm-meta">
                  {{ c.direction === 'out' ? 'Kiey Onboarding → ' + c.to : c.from + ' → Kiey Onboarding' }}
                  · {{ fmtDateTime(c.created_at) }}{{ c.status ? ' · ' + c.status : '' }}
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div class="dt-task-foot">
          <template v-if="!taskAsking">
            <button v-if="taskStep.state === 'done' || taskStep.complete" class="dt-btn dt-btn-ghost" :disabled="taskBusy" @click="reopenStep">↺ Reopen step</button>
            <button v-else class="dt-btn dt-btn-primary" :disabled="taskBusy" @click="taskAsking = 'confirm'">Mark done</button>
          </template>
          <div v-else-if="taskAsking === 'confirm'" class="dt-confirm">
            <span>Is this step complete?</span>
            <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="taskBusy" @click="confirmStepDone">Yes — complete</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="taskAsking = 'followup'">No — send a follow-up</button>
          </div>
          <div v-else class="dt-confirm">
            <span>Pick a follow-up to send:</span>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="taskBusy" @click="logFollowup('phone')">Phone</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="taskBusy" @click="logFollowup('sms')">SMS</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="taskBusy" @click="logFollowup('email')">Email</button>
          </div>
          <button class="dt-btn dt-btn-ghost" @click="closeTask">Close</button>
        </div>
      </div>
    </div>

    <!-- Full-page message reader -->
    <div v-if="commModal && commView" class="dt-overlay dt-overlay-top" @click.self="commModal = false">
      <div class="dt-modal dt-modal-comm">
        <button class="dt-modal-x" title="Close" @click="commModal = false">✕</button>
        <div class="dt-comm-view-tags">
          <span class="dt-dir-badge" :class="'dir-' + commView.direction">{{ commView.direction === 'out' ? 'Sent' : 'Received' }}</span>
          <span>{{ commView.channel === 'sms' ? '📱 Text' : '📧 Email' }}</span>
          <span v-if="commViewList.length > 1" class="dt-reader-nav">
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="commIndex <= 0" @click="navComm(-1)">‹ Prev</button>
            <span class="dt-muted">{{ commIndex + 1 }} / {{ commViewList.length }}</span>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :disabled="commIndex >= commViewList.length - 1" @click="navComm(1)">Next ›</button>
          </span>
        </div>
        <h3 class="dt-card-title">{{ commView.subject || (commView.channel === 'sms' ? 'Text message' : '(no subject)') }}</h3>
        <div class="dt-comm-meta">
          {{ commView.direction === 'out' ? 'Kiey Onboarding → ' + commView.to : commView.from + ' → Kiey Onboarding' }}
          · {{ fmtDateTime(commView.created_at) }}
          <span v-if="commView.channel === 'email' && commView.direction === 'out'"> · {{ commView.opened_at ? 'Opened ' + fmtDateTime(commView.opened_at) : 'Not opened' }}</span>
        </div>
        <div class="dt-comm-view-body">{{ commView.body }}</div>

        <!-- Reply directly -->
        <div class="dt-reply-box">
          <div class="dt-flabel">Reply via {{ commView.channel === 'sms' ? 'SMS' : 'email' }} to {{ replyRecipient(commView) || '—' }}</div>
          <input v-if="commView.channel === 'email'" v-model="replySubject" class="dt-input" placeholder="Subject" />
          <textarea v-model="replyBody" class="dt-input" rows="3" :placeholder="commView.channel === 'sms' ? 'Text reply…' : 'Email reply…'"></textarea>
          <div class="dt-foot-right">
            <button class="dt-btn dt-btn-ghost" @click="copyText(commView.body)">copy original</button>
            <button class="dt-btn dt-btn-primary" :disabled="replySending || !replyBody.trim() || !replyRecipient(commView)" @click="sendReply">{{ replySending ? 'sending…' : 'Send reply' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Single-field edit modal (one cell at a time) -->
    <div v-if="fieldModal && fieldTeam" class="dt-overlay">
      <div class="dt-modal">
        <button class="dt-modal-x" title="Close" @click="fieldModal = false">✕</button>
        <h3 class="dt-card-title">{{ fieldTeam.team_name }} — {{ fieldTitle }}</h3>

        <template v-if="fieldType === 'counts'">
          <div class="dt-fields-grid">
            <div class="dt-field"><span class="dt-flabel">Agents active</span><input v-model.number="fieldForm.agents_active" type="number" min="0" /></div>
            <div class="dt-field"><span class="dt-flabel">Agent roster (total)</span><input v-model.number="fieldForm.agents_target" type="number" min="0" /></div>
            <div class="dt-field"><span class="dt-flabel">Vendors active</span><input v-model.number="fieldForm.vendors_active" type="number" min="0" /></div>
            <div class="dt-field"><span class="dt-flabel">Vendors total</span><input v-model.number="fieldForm.vendors_total" type="number" min="0" /></div>
            <div class="dt-field"><span class="dt-flabel">Homeowners active</span><input v-model.number="fieldForm.homeowners_active" type="number" min="0" /></div>
            <div class="dt-field"><span class="dt-flabel">Homeowners total</span><input v-model.number="fieldForm.homeowners_total" type="number" min="0" /></div>
          </div>
        </template>

        <template v-else-if="fieldType === 'registered'">
          <div class="dt-field"><span class="dt-flabel">Registered date</span><input v-model="fieldForm.registered_date" type="date" /></div>
          <div class="dt-field dt-field-check"><label class="dt-check-label"><input v-model="fieldForm.intro_demo_done" type="checkbox" /> Intro demo done</label></div>
          <div class="dt-field"><span class="dt-flabel">Intro demo date</span><input v-model="fieldForm.intro_demo_date" type="date" /></div>
        </template>

        <template v-else-if="fieldType === 'followup'">
          <ul v-if="teamFus.length" class="dt-team-tasks">
            <li v-for="f in teamFus" :key="f.id" class="dt-team-task" :class="{ overdue: isOverdue(f) }">
              <input type="checkbox" title="Mark done" @change="completeFu(f.id); fieldModal = false" />
              <input v-model="f.title" class="dt-input dt-fu-edit-title" @change="saveFu(f)" />
              <input :value="toInputDate(f.due_date)" type="date" class="dt-input dt-fu-edit-date" @change="saveFu(f, $event.target.value)" />
            </li>
          </ul>
          <div class="dt-field"><span class="dt-flabel">New follow-up task</span><input v-model="fieldForm.title" type="text" placeholder="e.g. Call back about the vendor list" /></div>
          <div class="dt-field"><span class="dt-flabel">Due date (optional)</span><input v-model="fieldForm.due_date" type="date" /></div>
        </template>

        <template v-else-if="fieldType === 'note'">
          <div class="dt-field"><span class="dt-flabel">Add a note (timestamped)</span><textarea v-model="fieldForm.text" rows="3"></textarea></div>
          <ul v-if="fieldNotes.length" class="dt-updates">
            <li v-for="u in fieldNotes" :key="u.id" class="dt-update">
              <div class="dt-update-meta"><b>{{ u.author_name || 'Super User' }}</b><span class="dt-muted">{{ fmtDateTime(u.created_at) }}</span></div>
              <div class="dt-update-text">{{ u.text }}</div>
            </li>
          </ul>
        </template>

        <div class="dt-foot-right">
          <button class="dt-btn dt-btn-ghost" @click="fieldModal = false">Cancel</button>
          <button class="dt-btn dt-btn-primary" :disabled="fieldSaving" @click="saveField">{{ fieldSaving ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- AI outreach modal -->
    <div v-if="outreachModal" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="closeOutreach">✕</button>
        <h3 class="dt-card-title">✉ Outreach to {{ outreachTeamName }}</h3>
        <p v-if="outreachLoading" class="dt-empty">Drafting stage-aware outreach with AI…</p>
        <template v-else-if="outreachData">
          <div class="dt-field">
            <span class="dt-flabel">Subject <button class="dt-copy" @click="copyText(outreachData.subject)">copy</button></span>
            <input :value="outreachData.subject" readonly @focus="$event.target.select()" />
          </div>
          <div class="dt-field">
            <span class="dt-flabel">Email <button class="dt-copy" @click="copyText(outreachData.email)">copy</button></span>
            <textarea :value="outreachData.email" rows="9" readonly></textarea>
          </div>
          <div v-if="outreachData.sms" class="dt-field">
            <span class="dt-flabel">Text message <button class="dt-copy" @click="copyText(outreachData.sms)">copy</button></span>
            <textarea :value="outreachData.sms" rows="3" readonly></textarea>
          </div>
        </template>
        <p v-else class="dt-empty">Couldn’t draft outreach. Try again.</p>
        <div class="dt-foot-right">
          <button class="dt-btn dt-btn-ghost" @click="closeOutreach">Close</button>
          <button class="dt-btn dt-btn-ghost" :disabled="outreachLoading" @click="loadOutreach">
            {{ outreachLoading ? 'Drafting…' : 'Regenerate' }}
          </button>
          <button
            v-if="outreachData && outreachData.email"
            class="dt-btn dt-btn-primary"
            :disabled="sending"
            @click="sendEmail(outreachTeamId, outreachData.subject, outreachData.email)"
          >
            {{ sending ? 'Sending…' : '✈ Send email to admin' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit action STEPS overlay (the playbook every team flows through) -->
    <div v-if="stepsModal" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="closeSteps">✕</button>
        <h3 class="dt-card-title">Edit onboarding action steps</h3>
        <p class="dt-task-desc">The playbook every team flows through. Drag steps to reorder, edit names, sub-steps, and which live metric each ties to for auto-completion. Add or remove steps.</p>
        <p v-if="stepsLoading" class="dt-empty">Loading…</p>
        <ol v-else class="dt-steps-edit">
          <li
            v-for="(st, i) in editSteps"
            :key="i"
            class="dt-step-edit"
            :class="{ dragover: stepDragOverIndex === i }"
            :draggable="dragArmed"
            @dragstart="stepDragIndex = i"
            @dragover.prevent="stepDragOverIndex = i"
            @drop="onStepDrop(i)"
            @dragend="stepDragIndex = null; stepDragOverIndex = null; dragArmed = false"
          >
            <div class="dt-step-edit-head">
              <span class="dt-drag" title="Drag to reorder" @mousedown="dragArmed = true" @mouseup="dragArmed = false">⠿</span>
              <span class="dt-rank">{{ i + 1 }}</span>
              <input v-model="st.label" class="dt-input" placeholder="Step name" />
              <select v-model="st.metric" class="dt-step-metric" title="Auto-completes when this metric is met">
                <option v-for="m in stepMetrics" :key="m" :value="m">{{ m }}</option>
              </select>
              <button class="dt-task-del" title="Remove step" @click="editSteps.splice(i, 1)">✕</button>
            </div>
            <textarea v-model="st.description" class="dt-input dt-step-desc-input" rows="2" placeholder="Guidance for the employee"></textarea>
            <div class="dt-substeps-edit">
              <div class="dt-flabel">Sub-steps</div>
              <div
                v-for="(ss, j) in st.sub_steps"
                :key="j"
                class="dt-substep-row"
                :class="{ dragover: subDragStep === i && subDragOver === j }"
                :draggable="dragArmed"
                @dragstart.stop="onSubDragStart(i, j)"
                @dragover.prevent.stop="onSubDragOver(i, j)"
                @drop.stop="onSubDrop(i, j)"
                @dragend.stop="resetSubDrag(); dragArmed = false"
              >
                <span class="dt-drag dt-sub-drag" title="Drag to reorder" @mousedown="dragArmed = true" @mouseup="dragArmed = false">⠿</span>
                <span class="dt-substep-num">{{ j + 1 }}.</span>
                <input v-model="st.sub_steps[j]" class="dt-input" placeholder="Sub-step" />
                <button class="dt-task-del" @click="st.sub_steps.splice(j, 1)">✕</button>
              </div>
              <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="st.sub_steps.push('')">+ sub-step</button>
            </div>
          </li>
        </ol>
        <button v-if="!stepsLoading" class="dt-btn dt-btn-ghost dt-add-step" @click="addStep">+ Add step</button>
        <div class="dt-foot-right">
          <button class="dt-btn dt-btn-ghost" @click="closeSteps">Cancel</button>
          <button class="dt-btn dt-btn-primary" :disabled="savingSteps" @click="saveStepsEdit">{{ savingSteps ? 'Saving…' : 'Save steps' }}</button>
        </div>
      </div>
    </div>

    <!-- Messaging — unified inbox across all teams -->
    <div v-if="messagingModal" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="messagingModal = false">✕</button>
        <h3 class="dt-card-title">📨 Messaging <span v-if="allComms.length" class="dt-count">{{ allComms.length }}</span></h3>
        <p class="dt-task-desc">Every email &amp; text across all teams, newest first. Click a message to read it and reply.</p>

        <!-- Unrouted-reply triage: replies we couldn't match to a team -->
        <div v-if="unroutedComms.length" class="dt-unrouted">
          <div class="dt-unrouted-title">⚠️ Unrouted replies <span class="dt-count">{{ unroutedComms.length }}</span> — assign each to a team so it's not lost</div>
          <div v-for="c in unroutedComms" :key="c.id" class="dt-unrouted-row">
            <span class="dt-comm-icon">{{ c.channel === 'sms' ? '📱' : '📧' }}</span>
            <div class="dt-unrouted-main">
              <div class="dt-unrouted-from">{{ c.from }} · {{ fmtDateTime(c.created_at) }}</div>
              <div class="dt-unrouted-preview">{{ c.subject || (c.body || '').slice(0, 80) }}</div>
            </div>
            <select v-model="assignChoice[c.id]" class="dt-input dt-unrouted-select">
              <option value="">Assign to…</option>
              <option v-for="t in teams" :key="t.id" :value="t.id">{{ t.team_name }}</option>
            </select>
            <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="!assignChoice[c.id] || assigning === c.id" @click="assignUnrouted(c)">{{ assigning === c.id ? '…' : 'Assign' }}</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm dt-unrouted-delete" :disabled="dismissing === c.id" title="Delete this reply (junk / wrong number)" @click="dismissUnrouted(c)">{{ dismissing === c.id ? '…' : 'Delete' }}</button>
          </div>
        </div>

        <input v-model="msgSearch" class="dt-input dt-msg-search" type="search" placeholder="🔍 Search messages — name, team, or any text…" />
        <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: msgAiOnly }" title="Show only what the AI Autopilot sent + the replies it got" @click="msgAiOnly = !msgAiOnly">🤖 AI only</button>
        <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="msgComposeOpen = !msgComposeOpen">{{ msgComposeOpen ? '× Cancel' : '✏️ New message' }}</button>
        <div v-if="msgComposeOpen" class="dt-compose">
          <div class="dt-channel-btns">
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: msgChannel === 'email' }" @click="msgChannel = 'email'">✉ Email</button>
            <button class="dt-btn dt-btn-ghost dt-btn-sm" :class="{ active: msgChannel === 'sms' }" @click="msgChannel = 'sms'">📱 SMS</button>
          </div>
          <select v-model="msgTeamId" class="dt-input" @change="msgPickTeam">
            <option value="">Pick a team…</option>
            <option v-for="t in teams" :key="t.id" :value="t.id">{{ t.team_name }}</option>
          </select>
          <div v-if="msgTeamId" class="dt-send-row">
            <contact-picker :contacts="msgContacts" :field="msgChannel === 'sms' ? 'phone' : 'email'" :value="msgTo" :placeholder="msgChannel === 'sms' ? 'search a contact or type any phone' : 'search a contact or type any email'" @input="msgTo = $event" />
          </div>
          <div v-if="msgTo && !recipientValid(msgTo, msgChannel)" class="dt-input-err">Enter a valid {{ msgChannel === 'sms' ? 'phone number' : 'email address' }}.</div>
          <select v-if="templatesFor(msgChannel, 'msg').length" class="dt-input dt-tpl-pick" @change="applyTemplate($event.target.value, 'msg'); $event.target.value = ''">
            <option value="">📋 Insert template…</option>
            <option v-for="t in templatesFor(msgChannel, 'msg')" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
          <input v-if="msgChannel === 'email'" v-model="msgSubject" class="dt-input" placeholder="Subject" />
          <textarea ref="msgBodyEl" v-model="msgBody" class="dt-input dt-compose-body" rows="4" :placeholder="msgChannel === 'sms' ? 'Text message…' : 'Email message…'" @input="autoGrow($event.target)"></textarea>
          <div v-if="msgChannel === 'email' && outreach.signature" class="dt-sig-preview"><span class="dt-sig-preview-label">Signature (added automatically)</span><br>{{ outreach.signature }}</div>
          <div v-if="msgChannel === 'sms' && smsWillAppendStop(msgTo, msgContacts)" class="dt-sig-preview"><span class="dt-sig-preview-label">Compliance</span><br>"Reply STOP to opt out" is auto-added only on the first text to a non-Kiey number — existing Kiey users are skipped.</div>
          <div class="dt-foot-row">
            <button v-if="msgChannel === 'email' && outreach.attachments.length" type="button" class="dt-attach-btn" :class="{ active: msgAttachUrls.length }" title="Attach files" @click="openAttach('msg')">📎<span v-if="msgAttachUrls.length" class="dt-attach-count">{{ msgAttachUrls.length }}</span></button>
            <span class="dt-foot-spacer"></span>
            <button class="dt-btn dt-btn-primary dt-btn-sm" :disabled="msgSending || !msgTeamId || !recipientValid(msgTo, msgChannel) || !msgBody.trim()" @click="msgSend">{{ msgSending ? 'sending…' : ('Send ' + (msgChannel === 'sms' ? 'SMS' : 'email')) }}</button>
          </div>
        </div>
        <p v-if="allCommsLoading" class="dt-empty">Loading…</p>
        <p v-else-if="allComms.length === 0" class="dt-empty">No messages yet.</p>
        <p v-else-if="filteredAllComms.length === 0" class="dt-empty">No messages match "{{ msgSearch }}".</p>
        <ul v-else class="dt-comms-list dt-comms-list-tall">
          <li v-for="c in filteredAllComms" :key="c.id" class="dt-comm dt-comm-click" :class="['dir-' + c.direction, { unreadrow: c.direction === 'in' && !c.read }]" @click="openCommView(c, filteredAllComms)">
            <span class="dt-comm-icon">{{ c.channel === 'sms' ? '📱' : '📧' }}</span>
            <div class="dt-comm-body">
              <div class="dt-comm-head">
                <span class="dt-dir-badge" :class="'dir-' + c.direction">{{ c.direction === 'out' ? 'Sent' : 'Received' }}</span>
                <span class="dt-comm-team">{{ c.team_name }}</span>
                <span class="dt-comm-subject-line">{{ c.subject || (c.body || '').slice(0, 60) }}</span>
                <span v-if="c.direction === 'in' && !c.read" class="dt-read-chip dt-new-flag">NEW</span>
                <span v-else-if="c.channel === 'email' && c.direction === 'out'" class="dt-read-chip" :class="readClass(c)">{{ readLabel(c) }}</span>
                <span class="dt-comm-caret">›</span>
              </div>
              <div class="dt-comm-meta">
                {{ c.direction === 'out' ? 'Kiey Onboarding → ' + c.to : c.from + ' → Kiey Onboarding' }}
                · {{ fmtDateTime(c.created_at) }}
              </div>
            </div>
          </li>
        </ul>
        <div class="dt-foot-right"><button class="dt-btn dt-btn-primary" @click="messagingModal = false">Done</button></div>
      </div>
    </div>

    <!-- AI Activity — everything the Onboarding Autopilot did, grouped by day -->
    <div v-if="aiActivityModal" class="dt-overlay" @click.self="aiActivityModal = false">
      <div class="dt-modal dt-modal-wide" style="width:96vw;max-width:1200px;height:94vh;max-height:94vh;display:flex;flex-direction:column">
        <button class="dt-modal-x" title="Close" @click="aiActivityModal = false">✕</button>
        <h3 class="dt-card-title">🤖 AI Activity <span v-if="aiItems.length" class="dt-count">{{ aiItems.length }}</span></h3>
        <p class="dt-task-desc">Everything the Onboarding Autopilot has sent — and the replies it got — grouped by day, newest first.</p>
        <div v-if="!aiLoading && aiSummary && (aiSummary.sends || aiSummary.replies)" style="margin:6px 0 14px">
          <div class="dt-ai-statrow">
            <div class="dt-ai-rowlabel">Overall</div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">Messages sent</div><div class="dt-ai-stat-n">{{ aiSummary.sends }}</div></div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">Last 7 days</div><div class="dt-ai-stat-n">{{ aiSummary.sends_7d || 0 }}</div></div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">Reply rate</div><div class="dt-ai-stat-n" style="color:#19A99D">{{ aiSummary.reply_rate }}%</div><div class="dt-ai-stat-s">{{ aiSummary.replies }} replies</div></div>
            <div class="dt-ai-stat" title="Pixel-tracked opens ÷ emails sent. Undercounts (Apple Mail Privacy, image blocking) — treat as a floor."><div class="dt-ai-stat-l">Email open rate</div><div class="dt-ai-stat-n" style="color:#19A99D">{{ aiSummary.open_rate || 0 }}%</div><div class="dt-ai-stat-s">{{ aiSummary.emails_opened || 0 }}/{{ aiSummary.email || 0 }} opened</div></div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">Teams contacted</div><div class="dt-ai-stat-n">{{ aiSummary.teams_contacted }}</div></div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">By channel</div><div class="dt-ai-stat-n" style="font-size:14px">{{ aiSummary.email }} email · {{ aiSummary.sms }} sms</div></div>
            <div class="dt-ai-stat dt-ai-stat-click" title="See who opted out" @click="openOptOuts"><div class="dt-ai-stat-l">Opt-outs ›</div><div class="dt-ai-stat-n" :style="{ color: aiSummary.opt_outs > 0 ? '#DC3545' : '#19A99D' }">{{ aiSummary.opt_outs }}</div></div>
            <div class="dt-ai-stat dt-ai-stat-click" title="Dead email addresses (bounced) — suppressed, but not deliberate opt-outs" @click="openOptOuts"><div class="dt-ai-stat-l">Bounced ›</div><div class="dt-ai-stat-n" :style="{ color: (aiSummary.bounces || 0) > 0 ? '#B7791F' : '#19A99D' }">{{ aiSummary.bounces || 0 }}</div></div>
          </div>
          <div class="dt-ai-statrow" style="margin-top:8px">
            <div class="dt-ai-rowlabel" style="color:#19A99D">Today</div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">Messages sent</div><div class="dt-ai-stat-n">{{ (aiSummary.today && aiSummary.today.sends) || 0 }}</div></div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">Replies</div><div class="dt-ai-stat-n">{{ (aiSummary.today && aiSummary.today.replies) || 0 }}</div></div>
            <div class="dt-ai-stat" title="Opens so far on emails sent today — climbs as recipients open them"><div class="dt-ai-stat-l">Email open rate</div><div class="dt-ai-stat-n" style="color:#19A99D">{{ (aiSummary.today && aiSummary.today.open_rate) || 0 }}%</div><div class="dt-ai-stat-s">{{ (aiSummary.today && aiSummary.today.emails_opened) || 0 }}/{{ (aiSummary.today && aiSummary.today.email) || 0 }} opened</div></div>
            <div class="dt-ai-stat"><div class="dt-ai-stat-l">By channel</div><div class="dt-ai-stat-n" style="font-size:14px">{{ (aiSummary.today && aiSummary.today.email) || 0 }} email · {{ (aiSummary.today && aiSummary.today.sms) || 0 }} sms</div></div>
            <div class="dt-ai-stat dt-ai-stat-click" title="See who opted out" @click="openOptOuts"><div class="dt-ai-stat-l">Opt-outs ›</div><div class="dt-ai-stat-n" :style="{ color: (aiSummary.today && aiSummary.today.opt_outs) > 0 ? '#DC3545' : '#19A99D' }">{{ (aiSummary.today && aiSummary.today.opt_outs) || 0 }}</div></div>
            <div class="dt-ai-stat dt-ai-stat-click" title="Dead email addresses (bounced) today" @click="openOptOuts"><div class="dt-ai-stat-l">Bounced ›</div><div class="dt-ai-stat-n" :style="{ color: (aiSummary.today && aiSummary.today.bounces) > 0 ? '#B7791F' : '#19A99D' }">{{ (aiSummary.today && aiSummary.today.bounces) || 0 }}</div></div>
          </div>
        </div>
        <p v-if="aiLoading" class="dt-empty">Loading…</p>
        <p v-else-if="aiItems.length === 0" class="dt-empty">The autopilot hasn't sent anything yet. It runs hourly 9am–5pm ET — activity will appear here after its first run.</p>
        <template v-else>
          <!-- Date tabs across the top — click a day to view it; today selected by default -->
          <div class="dt-ai-daytabs">
            <button
              v-for="grp in aiByDay" :key="grp.day" type="button"
              class="dt-ai-daytab" :class="{ active: aiActiveDay === grp.day }"
              @click="aiActiveDay = grp.day"
            >{{ grp.label }} <span class="dt-count">{{ grp.items.length }}</span></button>
          </div>
          <div style="flex:1;overflow-y:auto;min-height:0;padding-right:6px">
            <ul class="dt-comms-list dt-comms-list-flow">
              <li v-for="c in activeDayItems" :key="c.id" class="dt-comm dt-comm-click" :class="'dir-' + c.direction" title="Click to open the full message" @click="openAiMessage(c)">
                <span class="dt-comm-icon">{{ c.channel === 'sms' ? '📱' : '📧' }}</span>
                <div class="dt-comm-body">
                  <div class="dt-comm-head">
                    <span class="dt-dir-badge" :class="'dir-' + c.direction">{{ c.direction === 'out' ? '🤖 AI sent' : 'Reply' }}</span>
                    <span class="dt-comm-team">{{ c.team_name }}</span>
                    <span v-if="c.subject" class="dt-comm-subj">{{ c.subject }}</span>
                    <span v-if="c.direction === 'out' && c.channel === 'email'" class="dt-open-badge" :class="c.opened_at ? 'opened' : 'unopened'">{{ c.opened_at ? '✓ Opened' : 'Not opened' }}</span>
                  </div>
                  <div class="dt-comm-meta">{{ c.direction === 'out' ? 'Kiey Onboarding → ' + c.to : c.from + ' → Kiey Onboarding' }} · {{ fmtDateTime(c.created_at) }}</div>
                  <div class="dt-comm-preview">{{ c.body }}</div>
                </div>
              </li>
            </ul>
          </div>
        </template>
        <div class="dt-foot-right"><button class="dt-btn dt-btn-primary" @click="aiActivityModal = false">Done</button></div>
      </div>
    </div>

    <!-- Single AI message — full screen so a long email is readable without scrolling a tiny card -->
    <div v-if="aiMessageModal && aiMessageSel" class="dt-overlay dt-overlay-top" @click.self="aiMessageModal = false">
      <div class="dt-modal" style="width:96vw;max-width:880px;height:94vh;max-height:94vh;display:flex;flex-direction:column">
        <button class="dt-modal-x" title="Close" @click="aiMessageModal = false">✕</button>
        <h3 class="dt-card-title">
          {{ aiMessageSel.channel === 'sms' ? '📱' : '📧' }}
          <span class="dt-dir-badge" :class="'dir-' + aiMessageSel.direction">{{ aiMessageSel.direction === 'out' ? '🤖 AI sent' : 'Reply' }}</span>
          {{ aiMessageSel.team_name }}
        </h3>
        <div class="dt-comm-meta" style="margin-bottom:4px">
          {{ aiMessageSel.direction === 'out' ? 'Kiey Onboarding → ' + aiMessageSel.to : aiMessageSel.from + ' → Kiey Onboarding' }} · {{ fmtDateTime(aiMessageSel.created_at) }}
          <span v-if="aiMessageSel.direction === 'out' && aiMessageSel.channel === 'email'" class="dt-open-badge" :class="aiMessageSel.opened_at ? 'opened' : 'unopened'">{{ aiMessageSel.opened_at ? '✓ Opened ' + fmtDateTime(aiMessageSel.opened_at) : 'Not opened yet' }}</span>
        </div>
        <div v-if="aiMessageSel.subject" style="font-weight:700;color:#061D47;font-size:16px;margin:8px 0">{{ aiMessageSel.subject }}</div>
        <div style="flex:1;overflow-y:auto;min-height:0;white-space:pre-wrap;font-size:15px;line-height:1.5;color:#222;padding:14px 16px;background:#f6f8fb;border-radius:8px">{{ aiMessageSel.body }}</div>
        <div class="dt-foot-right"><button class="dt-btn dt-btn-primary" @click="aiMessageModal = false">Done</button></div>
      </div>
    </div>

    <!-- Opt-outs — who asked to stop, with role + team -->
    <div v-if="optOutModal" class="dt-overlay dt-overlay-top" @click.self="optOutModal = false">
      <div class="dt-modal" style="width:96vw;max-width:720px;max-height:90vh;display:flex;flex-direction:column">
        <button class="dt-modal-x" title="Close" @click="optOutModal = false">✕</button>
        <h3 class="dt-card-title">🚫 Opt-outs <span v-if="optOutRows.length" class="dt-count">{{ optOutRows.length }}</span></h3>
        <p class="dt-task-desc">People who replied STOP / unsubscribe (or bounced) — suppressed from further outreach.</p>
        <p v-if="!optOutRows.length" class="dt-empty">No opt-outs. 🎉</p>
        <div v-else style="flex:1;overflow-y:auto;min-height:0">
          <table class="dt-optout-table">
            <thead><tr><th>Name</th><th>Role</th><th>Team</th><th>Contact</th><th>When</th></tr></thead>
            <tbody>
              <tr v-for="(o, i) in optOutRows" :key="i">
                <td>{{ o.name || '—' }}</td>
                <td>{{ o.role || '—' }}</td>
                <td>{{ o.team_name || '—' }}</td>
                <td>{{ o.contact }}<span v-if="o.bounced" class="dt-open-badge unopened" style="margin-left:6px">bounced</span></td>
                <td>{{ o.at ? fmtDateTime(o.at) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="dt-foot-right"><button class="dt-btn dt-btn-primary" @click="optOutModal = false">Done</button></div>
      </div>
    </div>

    <!-- Action History — every action taken, by date, filterable by account -->
    <div v-if="historyModal" class="dt-overlay" @click.self="historyModal = false">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="historyModal = false">✕</button>
        <h3 class="dt-card-title">🕘 Action History</h3>
        <div class="dt-hist-views">
          <button class="dt-hist-view" :class="{ active: historyView === 'ours' }" @click="setHistoryView('ours')">Our activity</button>
          <button class="dt-hist-view" :class="{ active: historyView === 'account' }" @click="setHistoryView('account')">Account activity</button>
        </div>
        <p class="dt-sub">{{ historyView === 'account' ? 'What each account\'s owners + admins did in the app — so you know what they\'ve handled and can reach out.' : 'What our team has done — emails, texts, steps, status changes.' }}</p>
        <div class="dt-hist-filters">
          <select v-model="historyTeamFilter" class="dt-input dt-hist-team" @change="onHistoryTeamChange">
            <option value="">All accounts</option>
            <option v-for="t in teams" :key="t.id" :value="t.id">{{ t.team_name }}</option>
          </select>
          <div class="dt-hist-chips">
            <button v-for="opt in currentTypeOptions" :key="opt.key" class="dt-hist-chip" :class="{ active: historyTypeFilter === opt.key }" @click="historyTypeFilter = opt.key">{{ opt.label }}</button>
          </div>
          <div class="dt-hist-chips">
            <button v-for="d in historyDayOptions" :key="d.v" class="dt-hist-chip" :class="{ active: historyDays === d.v }" @click="setHistoryDays(d.v)">{{ d.label }}</button>
          </div>
        </div>

        <!-- OUR activity (deployment team's outreach + step/status changes) -->
        <template v-if="historyView === 'ours'">
          <p v-if="historyLoading" class="dt-empty">Loading…</p>
          <p v-else-if="!historyGrouped.length" class="dt-empty">No actions in this range.</p>
          <div v-else class="dt-hist-feed">
            <template v-for="grp in historyGrouped">
              <div :key="'d' + grp.label" class="dt-hist-day">{{ grp.label }}</div>
              <div v-for="r in grp.rows" :key="r.id" class="dt-hist-row">
                <span class="dt-hist-time">{{ fmtTime(r.at) }}</span>
                <span class="dt-hist-icon">{{ activityIcon(r.type) }}</span>
                <span class="dt-hist-summary">{{ r.summary }}<span v-if="r.detail" class="dt-hist-detail"> — {{ r.detail }}</span></span>
                <span v-if="!historyTeamFilter && teamNameById(r.team_id)" class="dt-hist-team-tag">{{ teamNameById(r.team_id) }}</span>
                <span v-if="r.by_name" class="dt-hist-by">by {{ r.by_name }}</span>
              </div>
            </template>
          </div>
        </template>

        <!-- ACCOUNT activity (what the account's own users did) + Contact button -->
        <template v-else>
          <p v-if="userActivityLoading" class="dt-empty">Loading…</p>
          <p v-else-if="!userActivityGrouped.length" class="dt-empty">No account activity in this range.</p>
          <div v-else class="dt-hist-feed">
            <template v-for="grp in userActivityGrouped">
              <div :key="'ua' + grp.label" class="dt-hist-day">{{ grp.label }}</div>
              <div v-for="r in grp.rows" :key="r.id" class="dt-hist-row">
                <span class="dt-hist-time">{{ fmtTime(r.at) }}</span>
                <span class="dt-hist-icon">{{ categoryIcon(r.category) }}</span>
                <span class="dt-hist-summary">
                  <b>{{ r.actor_name || r.contact_phone || 'A user' }}</b><span v-if="r.actor_role" class="dt-hist-role"> ({{ r.actor_role }})</span>
                  {{ r.action }}<span v-if="r.target" class="dt-hist-detail"> — {{ r.target }}</span>
                </span>
                <span v-if="!historyTeamFilter && r.team_name" class="dt-hist-team-tag">{{ r.team_name }}</span>
                <button class="dt-btn dt-btn-ghost dt-btn-sm dt-hist-contact" :disabled="!r.team_id || (!r.contact_email && !r.contact_phone)" :title="r.team_id ? 'Email / text this user' : 'No deployment team linked to this account'" @click="contactFromActivity(r)">✉ Contact</button>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- Insights — response times, engagement, daily throughput -->
    <div v-if="insightsModal" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="insightsModal = false">✕</button>
        <h3 class="dt-card-title">📊 Insights</h3>
        <p v-if="insightsLoading" class="dt-empty">Loading…</p>
        <template v-else-if="insights">
          <h4 class="dt-flabel">✉️ Volume</h4>
          <div class="dt-insight-cards">
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.volume.email.sent_today }}</div><div class="dt-insight-l">Emails sent today<br>{{ insights.volume.email.sent_l7 }} in last 7 days</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.volume.email.recv_today }}</div><div class="dt-insight-l">Emails received today<br>{{ insights.volume.email.recv_l7 }} in last 7 days</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.volume.sms.sent_today }}</div><div class="dt-insight-l">SMS sent today<br>{{ insights.volume.sms.sent_l7 }} in last 7 days</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.volume.sms.recv_today }}</div><div class="dt-insight-l">SMS received today<br>{{ insights.volume.sms.recv_l7 }} in last 7 days</div></div>
          </div>
          <h4 class="dt-flabel">📱 SMS</h4>
          <div class="dt-insight-cards">
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.sms.reply_rate }}%</div><div class="dt-insight-l">Reply rate ({{ insights.sms.replied }}/{{ insights.sms.contacted }})</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ fmtDur(insights.sms.client_response_avg_sec) }}</div><div class="dt-insight-l">Avg client reply time</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ fmtDur(insights.sms.kiey_response_avg_sec) }}</div><div class="dt-insight-l">Avg Kiey reply time</div></div>
          </div>
          <h4 class="dt-flabel">📧 Email</h4>
          <div class="dt-insight-cards">
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.email.reply_rate }}%</div><div class="dt-insight-l">Reply rate ({{ insights.email.replied }}/{{ insights.email.contacted }})</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.email.open_rate }}%</div><div class="dt-insight-l">Open rate ({{ insights.email.emails_opened }}/{{ insights.email.emails_sent }})</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ fmtDur(insights.email.read_avg_sec) }}</div><div class="dt-insight-l">Avg time to open</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ fmtDur(insights.email.client_response_avg_sec) }}</div><div class="dt-insight-l">Avg client reply time</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ fmtDur(insights.email.kiey_response_avg_sec) }}</div><div class="dt-insight-l">Avg Kiey reply time</div></div>
          </div>
          <h4 class="dt-flabel">⚡ Daily throughput (last {{ insights.throughput.window_days }} days)</h4>
          <div class="dt-insight-cards">
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.throughput.tasks_per_day }}</div><div class="dt-insight-l">Avg tasks completed / day</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.throughput.steps_per_day }}</div><div class="dt-insight-l">Avg steps completed / day</div></div>
          </div>
          <h4 class="dt-flabel">🏢 Teams</h4>
          <div class="dt-insight-cards">
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.teams.contacted_today }}</div><div class="dt-insight-l">Teams contacted today<br>{{ insights.teams.contacted_l7 }} in last 7 days</div></div>
            <div class="dt-insight-card"><div class="dt-insight-n">{{ insights.teams.responded_today }}</div><div class="dt-insight-l">Teams responded today<br>{{ insights.teams.responded_l7 }} in last 7 days</div></div>
          </div>
          <h4 class="dt-flabel">🏆 Most → least engaged &amp; progressing</h4>
          <table class="dt-insight-table">
            <thead><tr><th>#</th><th>Team</th><th>Stage</th><th>Progress</th><th>Replies 7d</th><th>Steps 7d</th></tr></thead>
            <tbody>
              <tr v-for="(t, i) in insights.teams.ranking" :key="t.id">
                <td>{{ i + 1 }}</td>
                <td>{{ t.team_name }}</td>
                <td>{{ t.stage }}</td>
                <td>{{ t.progress }}%</td>
                <td>{{ t.replies_7d }}</td>
                <td>{{ t.steps_7d }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <div class="dt-foot-right"><button class="dt-btn dt-btn-primary" @click="insightsModal = false">Done</button></div>
      </div>
    </div>

    <!-- Settings — signature, templates, attachment library -->
    <div v-if="settingsModal" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="settingsModal = false">✕</button>
        <h3 class="dt-card-title">⚙ Outreach settings</h3>

        <h4 class="dt-flabel">Email signature</h4>
        <p class="dt-task-desc">Appended to every outreach email, above the unsubscribe footer.</p>
        <textarea v-model="settingsForm.signature" class="dt-input dt-compose-body" rows="4" placeholder="— Your name&#10;Kiey Onboarding Team&#10;phone / link"></textarea>

        <h4 class="dt-flabel">Templates</h4>
        <p class="dt-task-desc">Reusable subject + message you can drop into any compose box.</p>
        <div v-for="(t, i) in settingsForm.templates" :key="i" class="dt-tpl-row">
          <input v-model="t.name" class="dt-input dt-tpl-name" placeholder="Template name" />
          <select v-model="t.channel" class="dt-input dt-tpl-channel"><option value="email">Email</option><option value="sms">SMS</option></select>
          <input v-if="t.channel === 'email'" v-model="t.subject" class="dt-input" placeholder="Subject" />
          <textarea v-model="t.body" class="dt-input" rows="2" placeholder="Message…"></textarea>
          <button class="dt-row-delete" title="Remove template" @click="settingsForm.templates.splice(i, 1)">✕</button>
        </div>
        <button class="dt-btn dt-btn-ghost dt-btn-sm" @click="settingsForm.templates.push({ name: '', channel: 'email', subject: '', body: '' })">+ Add template</button>

        <h4 class="dt-flabel">Attachment library</h4>
        <p class="dt-task-desc">Upload reusable files (one-pagers, rate sheets) to attach when composing email.</p>
        <ul class="dt-att-list">
          <li v-for="(a, i) in settingsForm.attachments" :key="a.id || i" class="dt-att-item">
            <span class="dt-att-clip">📎</span>
            <input v-model="a.name" class="dt-input dt-att-name-input" placeholder="Attachment name" />
            <button class="dt-row-delete" title="Remove" @click="settingsForm.attachments.splice(i, 1)">✕</button>
          </li>
        </ul>
        <label class="dt-btn dt-btn-ghost dt-btn-sm dt-upload-label">
          {{ uploadingAtt ? 'Uploading…' : '+ Upload file' }}
          <input type="file" style="display:none" :disabled="uploadingAtt" @change="uploadAttachment" />
        </label>

        <div class="dt-foot-right">
          <button class="dt-btn dt-btn-ghost" @click="settingsModal = false">Cancel</button>
          <button class="dt-btn dt-btn-primary" :disabled="savingSettings" @click="saveSettingsForm">{{ savingSettings ? 'Saving…' : 'Save settings' }}</button>
        </div>
      </div>
    </div>

    <!-- Daily tasks modal -->
    <div v-if="dailyModal" class="dt-overlay">
      <div class="dt-modal dt-modal-wide">
        <button class="dt-modal-x" title="Close" @click="dailyModal = false">✕</button>
        <h3 class="dt-card-title">📋 Daily tasks <span v-if="followups.length" class="dt-count">{{ followups.length }}</span></h3>
        <p class="dt-task-desc">Follow-ups created from your notes. Overdue and due-today are at the top. Check one off to complete it.</p>
        <p v-if="followups.length === 0" class="dt-empty">No open follow-up tasks. 🎉</p>
        <ul v-else class="dt-daily-list">
          <li v-for="f in sortedFollowups" :key="f.id" class="dt-daily-item" :class="{ overdue: isOverdue(f), today: isToday(f) }">
            <input type="checkbox" @change="completeFu(f.id)" />
            <div class="dt-daily-main">
              <div class="dt-daily-title">{{ f.title }}</div>
              <div class="dt-daily-sub">
                <span class="dt-team-dot" :style="{ background: teamColor(f.team_id) }"></span>
                <b class="dt-link" @click="openTeamById(f.team_id); dailyModal = false">{{ f.team_name }}</b>
                <span v-if="f.due_date" class="dt-daily-due">· {{ isOverdue(f) ? 'overdue ' : '' }}{{ fmtDate(f.due_date) }}</span>
                <span v-else class="dt-muted">· no date</span>
              </div>
            </div>
          </li>
        </ul>
        <div class="dt-foot-right">
          <button class="dt-btn dt-btn-primary" @click="dailyModal = false">Done</button>
        </div>
      </div>
    </div>

    <!-- Create modal -->
    <div v-if="createModal" class="dt-overlay">
      <div class="dt-modal">
        <button class="dt-modal-x" title="Close" @click="createModal = false">✕</button>
        <h3 class="dt-card-title">Add a team</h3>
        <div class="dt-field"><span class="dt-flabel">Team name</span><input v-model="createForm.team_name" type="text" placeholder="e.g. Dallas" /></div>
        <div class="dt-field"><span class="dt-flabel">Status</span>
          <select v-model="createForm.status"><option>Prospect</option><option>Free</option><option>Paying</option></select>
        </div>
        <div class="dt-field"><span class="dt-flabel">Agent roster size (optional)</span><input v-model.number="createForm.agents_target" type="number" min="0" /></div>
        <div class="dt-foot-right">
          <button class="dt-btn dt-btn-ghost" @click="createModal = false">Cancel</button>
          <button class="dt-btn dt-btn-primary" :disabled="!createForm.team_name.trim() || creating" @click="doCreate">
            {{ creating ? 'Creating…' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Attachment picker — pop-up overlay listing the library -->
    <div v-if="attachModal" class="dt-overlay dt-overlay-top" @click.self="attachModal = false">
      <div class="dt-modal dt-modal-sm">
        <button class="dt-modal-x" title="Close" @click="attachModal = false">✕</button>
        <h3 class="dt-card-title">📎 Attach files</h3>
        <p class="dt-task-desc">Pick files from your library to attach to this email.</p>
        <ul class="dt-att-pick-list">
          <li v-for="a in outreach.attachments" :key="a.id" class="dt-att-pick-item">
            <label><input type="checkbox" :value="a.url" v-model="currentAttach" /> <span class="dt-att-clip">📎</span> {{ a.name }}</label>
          </li>
          <li v-if="!outreach.attachments.length" class="dt-empty">No files yet — upload some in ⚙ Settings.</li>
        </ul>
        <div class="dt-foot-right"><button class="dt-btn dt-btn-primary" @click="attachModal = false">Done<span v-if="currentAttach.length"> ({{ currentAttach.length }})</span></button></div>
      </div>
    </div>

    <!-- (Close-prompt modal removed — daily status is set via the clickable
         In Progress / Done chip on each row.) -->

    <div v-if="toast" class="dt-toast" :class="toastType">{{ toast }}</div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { storage } from '@/config/firebase';
import ContactPicker from './ContactPicker.vue';

function emptyForm() {
  return {
    team_name: '', status: 'Prospect', tenant_id: null, assigned_to: '',
    registered_date: '', admin_name: '', admin_email: '', admin_phone: '',
    intro_demo_done: false, intro_demo_date: '',
    agents_active: 0, agents_total: 0, agents_target: 0,
    vendors_active: 0, vendors_total: 0,
    homeowners_active: 0, homeowners_total: 0,
    marketing_packages: '', next_meeting_date: '', next_meeting_note: '', notes: '',
    counts_source: 'manual',
  };
}

const VENDOR_TARGET = 40;
const HOMEOWNER_TARGET = 100;
const SYNC_KEYS = ['agents_remaining', 'vendors_min', 'vendors_target', 'homeowners', 'counts_stale'];

export default {
  name: 'DeploymentTracker',
  components: { ContactPicker },

  data() {
    return {
      loading: false,
      errorMsg: null,
      rowStepIdx: {},
      teamSearch: '',
      assignedFilter: '',
      dragArmed: false,
      rowDragArmed: false,
      rowDragIndex: null,
      rowDragOverIndex: null,
      pollTimer: null,
      sortKey: 'due',
      sortDir: 'asc',
      // AI outreach
      outreachModal: false,
      outreachLoading: false,
      outreachData: null,
      outreachTeamId: null,
      outreachTeamName: '',
      // guided task modal
      taskModal: false,
      taskTeamId: null,
      taskTeamName: '',
      taskStep: null,
      taskAsking: '', // '' | 'confirm' | 'followup'
      taskDraft: null,
      taskDraftChannel: null,
      taskDraftLoading: false,
      taskBusy: false,
      sending: false,
      smsSending: false,
      taskContacts: [],
      taskComms: [],
      taskAllSteps: [],
      taskStepIndex: 0,
      taskDraftCache: {},
      activeComms: [],
      activeContacts: [],
      profileChannel: 'email',
      profileTo: '',
      profileSubject: '',
      profileBody: '',
      profileDrafting: false,
      profileSending: false,
      commModal: false,
      commView: null,
      replyBody: '',
      replySubject: '',
      replySending: false,
      messagingModal: false,
      allComms: [],
      allCommsLoading: false,
      msgAiOnly: false,
      aiActivityModal: false,
      aiItems: [],
      aiSummary: {},
      aiLoading: false,
      aiMessageModal: false,
      aiMessageSel: null,
      aiActiveDay: null,
      optOutModal: false,
      unroutedComms: [],
      assignChoice: {},
      assigning: null,
      dismissing: null,
      insightsModal: false,
      insightsLoading: false,
      insights: null,
      historyModal: false,
      historyLoading: false,
      historyRows: [],
      historyTeamFilter: '',
      historyTypeFilter: 'all',
      historyDays: 30,
      historyTypeOptions: [
        { key: 'all', label: 'All' },
        { key: 'outreach', label: 'Emails/Texts' },
        { key: 'step', label: 'Steps' },
        { key: 'status', label: 'Status/Counts' },
        { key: 'note', label: 'Notes' },
        { key: 'comm', label: 'Replies' },
      ],
      historyDayOptions: [
        { v: 1, label: 'Today' },
        { v: 7, label: '7 days' },
        { v: 30, label: '30 days' },
        { v: 0, label: 'All' },
      ],
      historyView: 'ours', // 'ours' | 'account'
      userActivityRows: [],
      userActivityLoading: false,
      accountTypeOptions: [
        { key: 'all', label: 'All' },
        { key: 'user', label: 'Users' },
        { key: 'vendor', label: 'Vendors' },
        { key: 'profile', label: 'Profile' },
        { key: 'billing', label: 'Billing' },
      ],
      settingsModal: false,
      settingsForm: { signature: '', templates: [], attachments: [] },
      savingSettings: false,
      uploadingAtt: false,
      outreach: { signature: '', templates: [], attachments: [] },
      profileAttachUrls: [],
      msgAttachUrls: [],
      attachModal: false,
      attachTarget: 'profile', // 'profile' | 'msg'
      closePromptModal: false,
      commViewList: [],
      msgSearch: '',
      msgComposeOpen: false,
      msgTeamId: '',
      msgChannel: 'email',
      msgTo: '',
      msgSubject: '',
      msgBody: '',
      msgContacts: [],
      msgSending: false,
      taskEmailTo: '',
      taskSmsTo: '',
      // profile
      active: null,
      form: emptyForm(),
      updates: [],
      newUpdate: '',
      postingUpdate: false,
      editingNoteId: null,
      editNoteText: '',
      // auto-save
      hydrating: false,
      autoSaveTimer: null,
      saveState: 'idle', // idle | saving | saved
      syncing: false,
      resolvingKey: null,
      tenantQuery: '',
      tenantResults: [],
      tenantSearchTimer: null,
      // edit action-steps overlay
      stepsModal: false,
      stepsLoading: false,
      editSteps: [],
      stepMetrics: [],
      savingSteps: false,
      stepDragIndex: null,
      stepDragOverIndex: null,
      subDragStep: null,
      subDragFrom: null,
      subDragOver: null,
      // single-field edit modal
      fieldModal: false,
      fieldType: '',
      fieldTeam: null,
      fieldForm: {},
      fieldNotes: [],
      fieldSaving: false,
      // follow-up tasks
      dailyModal: false,
      followupPrompt: false,
      followupTitle: '',
      followupDate: '',
      // create
      createModal: false,
      createForm: { team_name: '', status: 'Prospect', agents_target: 0 },
      creating: false,
      // toast
      toast: '', toastType: 'ok',
    };
  },

  computed: {
    ...mapState('Deployment', ['teams', 'summary', 'followups', 'manualOrder']),

    sortedFollowups() {
      return [...this.followups].sort((a, b) => (a.due_date || Infinity) - (b.due_date || Infinity));
    },
    dueCount() {
      const end = this.endOfToday();
      return this.followups.filter((f) => !f.due_date || f.due_date <= end).length;
    },
    teamFollowups() {
      if (!this.active) return [];
      return this.followups.filter((f) => f.team_id === this.active.id);
    },
    teamFus() {
      if (!this.fieldTeam) return [];
      return this.followups.filter((f) => f.team_id === this.fieldTeam.id);
    },
    // Which permission groups are relevant to the step currently open.
    stepGroups() {
      const k = this.taskStep && this.taskStep.key;
      if (k === 'agents' || k === 'intro_demo') return ['Owner', 'Agent Admin', 'Agent', 'Team Contact'];
      if (k === 'vendors') return ['Agent Admin', 'Vendor', 'Owner', 'Team Contact'];
      if (k === 'homeowners') return ['Owner', 'Agent Admin', 'Client', 'Team Contact'];
      return null; // any
    },
    // Step-relevant contacts (flat) for the autocomplete picker.
    taskFilteredContacts() {
      const groups = this.stepGroups;
      return this.taskContacts.filter((c) => !groups || groups.includes(c.group));
    },
    // Action History: apply account + type-category filters client-side.
    historyFiltered() {
      const cat = this.historyTypeFilter;
      const team = this.historyTeamFilter;
      const inCat = (type) => {
        if (cat === 'all') return true;
        if (cat === 'outreach') return type === 'outreach_email' || type === 'outreach_sms';
        if (cat === 'step') return type === 'step' || type === 'step_update';
        if (cat === 'status') return type === 'status' || type === 'counts';
        if (cat === 'note') return type === 'note';
        if (cat === 'comm') return type === 'comm_route' || type === 'comm_dismiss';
        return true;
      };
      return this.historyRows.filter((r) => (!team || r.team_id === team) && inCat(r.type));
    },
    currentTypeOptions() {
      return this.historyView === 'account' ? this.accountTypeOptions : this.historyTypeOptions;
    },
    userActivityFiltered() {
      const cat = this.historyTypeFilter;
      const team = this.historyTeamFilter;
      return this.userActivityRows.filter((r) => (cat === 'all' || r.category === cat) && (!team || r.team_id === team));
    },
    userActivityGrouped() {
      return this.groupByDay(this.userActivityFiltered);
    },
    // Group the filtered feed into Today / Yesterday / dated buckets.
    historyGrouped() {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const todayMs = today.getTime();
      const yestMs = todayMs - 86400000;
      const label = (ms) => {
        const d = new Date(ms); d.setHours(0, 0, 0, 0);
        if (d.getTime() === todayMs) return 'Today';
        if (d.getTime() === yestMs) return 'Yesterday';
        const sameYear = d.getFullYear() === today.getFullYear();
        return new Date(ms).toLocaleDateString(undefined, sameYear
          ? { weekday: 'short', month: 'short', day: 'numeric' }
          : { month: 'short', day: 'numeric', year: 'numeric' });
      };
      const out = [];
      let cur = null;
      this.historyFiltered.forEach((r) => {
        const l = label(r.at);
        if (!cur || cur.label !== l) { cur = { label: l, rows: [] }; out.push(cur); }
        cur.rows.push(r);
      });
      return out;
    },
    // Only messages tied to the step currently open in the task modal.
    taskStepComms() {
      if (!this.taskStep) return this.taskComms;
      return this.taskComms.filter((c) => c.step_key === this.taskStep.key);
    },
    // Assign each team a distinct color from a curated palette by stable
    // (id-sorted) index, so no two teams collide like an HSL hash would.
    teamColorMap() {
      const palette = [
        '#e6194B', '#3cb44b', '#4363d8', '#f58231', '#911eb4', '#008080',
        '#f032e6', '#9A6324', '#800000', '#808000', '#000075', '#e67e22',
        '#16a085', '#2980b9', '#8e44ad', '#c0392b', '#27ae60', '#d35400',
        '#2c3e50', '#1abc9c', '#d81b60', '#5d4037', '#00897b', '#6a1b9a',
      ];
      const ids = this.teams.map((t) => t.id).sort();
      const map = {};
      ids.forEach((id, i) => { map[id] = palette[i % palette.length]; });
      return map;
    },
    fieldTitle() {
      return {
        counts: 'Onboarding counts', registered: 'Registered & demo',
        followup: 'Follow-up tasks', note: 'Notes',
      }[this.fieldType] || 'Edit';
    },
    // Follow-ups that are actionable now (overdue, due today, or undated).
    saveStateLabel() {
      if (this.saveState === 'saving') return 'Saving…';
      if (this.saveState === 'saved') return '✓ Saved';
      return 'Auto-saves';
    },

    taskDraftText: {
      get() {
        if (!this.taskDraft) return '';
        if (this.taskDraftChannel === 'sms') return this.taskDraft.sms;
        if (this.taskDraftChannel === 'phone') return this.taskDraft.phone;
        return this.taskDraft.email;
      },
      set(v) {
        if (!this.taskDraft) return;
        const f = this.taskDraftChannel === 'sms' ? 'sms' : (this.taskDraftChannel === 'phone' ? 'phone' : 'email');
        this.$set(this.taskDraft, f, v);
      },
    },
    taskDraftLabel() {
      if (this.taskDraftChannel === 'sms') return 'Text message';
      if (this.taskDraftChannel === 'phone') return 'Phone script';
      return 'Email';
    },
    // Distinct account owners across all teams (for the assignee filter).
    assignees() {
      return [...new Set(this.teams.map((t) => t.assigned_to).filter(Boolean))].sort();
    },
    commIndex() {
      if (!this.commView) return -1;
      return this.commViewList.findIndex((x) => x.id === this.commView.id);
    },
    // Messaging search — matches team name, subject, body, and from/to.
    // "AI only" narrows to strictly what the Onboarding Autopilot SENT (not
    // inbound replies) — for the full per-day AI view + replies, use the
    // dedicated AI Activity tab.
    filteredAllComms() {
      const q = (this.msgSearch || '').toLowerCase().trim();
      let list = this.allComms;
      if (this.msgAiOnly) {
        list = list.filter((c) => c.sent_by_name === 'Onboarding Autopilot');
      }
      if (!q) return list;
      return list.filter((c) => `${c.team_name || ''} ${c.subject || ''} ${c.body || ''} ${c.from || ''} ${c.to || ''}`.toLowerCase().includes(q));
    },
    // AI Activity grouped by calendar day (newest day first), for the AI tab.
    aiByDay() {
      const groups = {};
      const order = [];
      (this.aiItems || []).forEach((c) => {
        const ms = this.toMillis(c.created_at);
        const d = ms ? new Date(ms) : null;
        const day = d ? d.toISOString().slice(0, 10) : 'unknown';
        if (!groups[day]) {
          groups[day] = { day, label: d ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : 'Unknown date', items: [] };
          order.push(day);
        }
        groups[day].items.push(c);
      });
      return order.sort((a, b) => (a < b ? 1 : -1)).map((k) => groups[k]);
    },
    activeDayItems() {
      const days = this.aiByDay;
      if (!days.length) return [];
      const grp = days.find((g) => g.day === this.aiActiveDay) || days[0];
      return grp.items;
    },
    optOutRows() {
      return (this.aiSummary && this.aiSummary.opt_out_list) || [];
    },
    // Bound by the attachment-picker modal to whichever compose surface opened it.
    currentAttach: {
      get() { return this.attachTarget === 'msg' ? this.msgAttachUrls : this.profileAttachUrls; },
      set(v) { if (this.attachTarget === 'msg') this.msgAttachUrls = v; else this.profileAttachUrls = v; },
    },

    sortedTeams() {
      const q = (this.teamSearch || '').toLowerCase().trim();
      let teams = q
        ? this.teams.filter((t) => `${t.team_name || ''} ${t.admin_email || ''}`.toLowerCase().includes(q))
        : this.teams;
      // Filter by who owns the account (assigned_to).
      if (this.assignedFilter === '__unassigned') teams = teams.filter((t) => !t.assigned_to);
      else if (this.assignedFilter) teams = teams.filter((t) => t.assigned_to === this.assignedFilter);
      if (!this.sortKey) return teams;
      // Priority applied in every sort mode: an unread reply floats a team to
      // the very top, and a team already worked TODAY sinks to the bottom — so
      // the live worklist is always what's left to do, newest replies first.
      const priority = (a, b) => {
        const ua = a.unread_comms > 0 ? 1 : 0;
        const ub = b.unread_comms > 0 ? 1 : 0;
        if (ua !== ub) return ub - ua;
        const da = this.doneToday(a) ? 1 : 0;
        const db = this.doneToday(b) ? 1 : 0;
        if (da !== db) return da - db; // done-today → bottom
        return 0;
      };
      // Manual drag order overrides column sorting (priority still applies).
      if (this.sortKey === 'manual') {
        const order = this.manualOrder || [];
        const idx = (id) => { const i = order.indexOf(id); return i === -1 ? 1e6 : i; };
        return [...teams].sort((a, b) => priority(a, b)
          || idx(a.id) - idx(b.id) || (a.team_name || '').localeCompare(b.team_name || ''));
      }
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const val = (t) => {
        switch (this.sortKey) {
          case 'team_name': return (t.team_name || '').toLowerCase();
          case 'due': return this.effectiveDue(t);
          case 'followup': return t.next_followup_at || (t.open_followups > 0 ? 8.64e15 : Infinity);
          case 'status': return t.stage_index;
          case 'days_in_step': return t.days_in_step;
          case 'completion': return t.progress.overall;
          case 'registered': return this.toMillis(t.registered_date) || 0;
          case 'agents': return t.progress.agents;
          case 'vendors': return t.vendors_active;
          case 'homeowners': return t.homeowners_active;
          default: return 0;
        }
      };
      return [...teams].sort((a, b) => {
        // Unread → top, done-today → bottom (shared with the manual branch).
        const p = priority(a, b);
        if (p !== 0) return p;
        const va = val(a); const vb = val(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        // Tiebreak: longest in a single step first (so teams with nothing due
        // surface by how long they've been stuck), then name.
        if ((b.days_in_step || 0) !== (a.days_in_step || 0)) {
          return (b.days_in_step || 0) - (a.days_in_step || 0);
        }
        return (a.team_name || '').localeCompare(b.team_name || '');
      });
    },
  },

  watch: {
    form: {
      deep: true,
      handler() {
        if (this.hydrating || !this.active) return;
        this.scheduleAutoSave();
      },
    },
  },

  async created() {
    await this.refresh();
    // Default to the grouped due view (Today on top, with bucket separators). A
    // saved manual drag order stays in state and re-engages only when the operator
    // actually drags a row — so the board never loads with Today-due work buried.
    // Load the outreach library (templates + attachments) for the compose pickers.
    try { this.outreach = await this.fetchSettings(); } catch (_) { /* non-fatal */ }
    // Live refresh — pick up new inbound texts/emails (+ light the envelope)
    // without a manual refresh. Polls because deployment_* is server-only
    // (the client can't read it via a Firestore listener).
    this.pollTimer = setInterval(this.pollUpdates, 25000);
  },

  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },

  methods: {
    ...mapActions('Deployment', [
      'fetchTeams', 'createTeam', 'updateTeam', 'syncCounts', 'deleteTeam',
      'fetchUpdates', 'addUpdate', 'editNote', 'deleteNote', 'setDailyStatus', 'searchTenants',
      'reorderTasks', 'draftOutreach', 'setStepState', 'sendOutreach',
      'fetchSteps', 'saveSteps', 'addFollowup', 'completeFollowup', 'editFollowup',
      'fetchContacts', 'fetchInviteLink', 'sendSms', 'fetchComms', 'markCommsRead', 'fetchAllComms',
      'fetchUnrouted', 'assignComm', 'dismissComm', 'fetchActivity', 'fetchUserActivity', 'fetchAiActivity', 'fetchInsights', 'fetchSettings', 'saveSettings',
    ]),

    // ── follow-up tasks (daily to-do) ──
    endOfToday() { const d = new Date(); d.setHours(23, 59, 59, 999); return d.getTime(); },
    startOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); },
    isOverdue(f) { return f.due_date && f.due_date < this.startOfToday(); },
    effectiveDue(t) {
      if (t.next_followup_at) return t.next_followup_at;
      if (t.open_followups > 0) return this.startOfToday();
      // Normalize through toMillis — these fields can arrive as Firestore
      // Timestamp objects, and raw `{_seconds} + ms` arithmetic would NaN.
      const base = this.toMillis(t.status_entered_at) || this.toMillis(t.created_at)
        || this.toMillis(t.registered_date) || Date.now();
      return base + 3 * 86400000; // 3-day step rule
    },
    bucketOf(due) {
      if (due <= this.endOfToday()) return 'Today';
      // End of this calendar week (Sunday 23:59).
      const eow = new Date(); eow.setHours(23, 59, 59, 999);
      eow.setDate(eow.getDate() + (7 - (eow.getDay() || 7)));
      if (due <= eow.getTime()) return 'This week';
      if (due <= eow.getTime() + 7 * 86400000) return 'Next week';
      const now = new Date();
      const eom = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
      if (due <= eom) return 'This month';
      return 'Later';
    },
    dueBucket(t) { return this.bucketOf(this.effectiveDue(t)); },
    bucketSlug(b) { return b.toLowerCase().replace(/\s+/g, '-'); },
    // True before the first row of each due bucket so the grouped view renders a
    // labeled separator (Today / This week / …). Only in the due-sorted view —
    // other column sorts and a free manual drag order aren't bucket-contiguous.
    showSepBefore(i) {
      if (this.sortKey !== 'due') return false;
      const rows = this.sortedTeams;
      if (!rows[i]) return false;
      if (i === 0) return true;
      return this.dueBucket(rows[i]) !== this.dueBucket(rows[i - 1]);
    },
    isToday(f) { return f.due_date && f.due_date >= this.startOfToday() && f.due_date <= this.endOfToday(); },
    async saveFollowup() {
      const title = this.followupTitle.trim();
      if (!title || !this.active) return;
      try {
        await this.addFollowup({ id: this.active.id, title, due_date: this.followupDate || null });
        this.followupPrompt = false;
        this.followupTitle = '';
        this.followupDate = '';
        this.showToast('Follow-up task added', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Could not add task', 'err');
      }
    },
    async saveFu(f, dateStr) {
      try {
        await this.editFollowup({
          fid: f.id,
          title: (f.title || '').trim() || 'Follow-up',
          due_date: dateStr !== undefined ? (dateStr || null) : (this.toInputDate(f.due_date) || null),
        });
        this.showToast('Follow-up updated', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Could not update follow-up', 'err');
      }
    },
    async completeFu(fid) {
      try {
        await this.completeFollowup({ fid });
        this.showToast('Task completed', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Could not complete task', 'err');
      }
    },

    // ── action-steps editor ──
    async openSteps() {
      this.stepsModal = true;
      this.stepsLoading = true;
      try {
        const data = await this.fetchSteps();
        this.stepMetrics = data.metrics || ['intro_demo', 'agents', 'vendors', 'homeowners', 'manual'];
        this.editSteps = (data.steps || []).map((s) => ({
          key: s.key,
          label: s.label || '',
          description: s.description || '',
          metric: s.metric || 'manual',
          sub_steps: Array.isArray(s.sub_steps) ? [...s.sub_steps] : [],
        }));
      } catch (e) {
        this.showToast('Could not load steps', 'err');
      } finally {
        this.stepsLoading = false;
      }
    },
    closeSteps() { this.stepsModal = false; },
    addStep() {
      this.editSteps.push({ label: 'New step', description: '', metric: 'manual', sub_steps: [] });
    },
    onStepDrop(i) {
      const from = this.stepDragIndex;
      this.stepDragOverIndex = null;
      if (from === null || from === i) { this.stepDragIndex = null; return; }
      const arr = [...this.editSteps];
      const [m] = arr.splice(from, 1);
      arr.splice(i, 0, m);
      this.editSteps = arr;
      this.stepDragIndex = null;
    },
    onSubDragStart(i, j) { this.subDragStep = i; this.subDragFrom = j; },
    onSubDragOver(i, j) { if (this.subDragStep === i) this.subDragOver = j; },
    onSubDrop(i, j) {
      const from = this.subDragFrom;
      if (this.subDragStep !== i || from === null || from === j) { this.resetSubDrag(); return; }
      const arr = this.editSteps[i].sub_steps;
      const [m] = arr.splice(from, 1);
      arr.splice(j, 0, m);
      this.resetSubDrag();
    },
    resetSubDrag() { this.subDragStep = null; this.subDragFrom = null; this.subDragOver = null; },

    async saveStepsEdit() {
      const steps = this.editSteps
        .map((s) => ({ ...s, label: (s.label || '').trim(), sub_steps: (s.sub_steps || []).map((x) => (x || '').trim()).filter(Boolean) }))
        .filter((s) => s.label);
      if (steps.length === 0) { this.showToast('Add at least one step', 'err'); return; }
      this.savingSteps = true;
      try {
        await this.saveSteps(steps);
        this.showToast('Action steps saved', 'ok');
        this.stepsModal = false;
        this.refresh();
      } catch (e) {
        this.showToast('Save failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.savingSteps = false;
      }
    },

    async toggleSub(j) {
      const cur = Array.isArray(this.taskStep.sub_done) ? [...this.taskStep.sub_done] : [];
      const idx = cur.indexOf(j);
      const unchecking = idx !== -1;
      if (idx === -1) cur.push(j); else cur.splice(idx, 1);
      this.$set(this.taskStep, 'sub_done', cur);
      // Unchecking an item on a step that was marked done/pending means it's no
      // longer finished — reopen it so the board reflects the real state.
      const reopen = unchecking && (this.taskStep.state === 'done' || this.taskStep.state === 'pending');
      if (reopen) { this.$set(this.taskStep, 'state', 'todo'); this.$set(this.taskStep, 'complete', false); }
      try {
        await this.setStepState({ id: this.taskTeamId, stepKey: this.taskStep.key, sub_done: cur, ...(reopen ? { state: 'todo' } : {}) });
      } catch (_) { this.showToast('Could not save checklist', 'err'); }
      // Re-target the AI draft to the new current sub-step immediately.
      if (this.taskDraftChannel) this.draftChannel(this.taskDraftChannel);
      // Update the table's "Step 1a/1b…" badge to reflect the new current sub-step.
      this.fetchTeams({ silent: true });
    },

    async sendEmail(teamId, subject, body, toOverride) {
      const team = this.teams.find((t) => t.id === teamId);
      const to = toOverride || (team && team.admin_email);
      if (!to) { this.showToast('Pick a recipient (or add an admin email in Team details).', 'err'); return; }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Send this email to ${to} from onboarding@kiey.com?`)) return;
      this.sending = true;
      try {
        await this.sendOutreach({ id: teamId, to, subject, body, stepKey: this.taskStep && this.taskStep.key });
        this.showToast(`Sent to ${to}`, 'ok');
        this.loadTaskComms(teamId);
        if (this.active && this.active.id === teamId) {
          try { this.updates = await this.fetchUpdates(teamId); } catch (_) { /* non-fatal */ }
        }
      } catch (e) {
        this.showToast('Send failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.sending = false;
      }
    },

    // ── guided task (step) flow ──
    openTask(item, step) {
      this.taskTeamId = item.team_id;
      this.taskTeamName = item.team_name;
      const team = this.teams.find((t) => t.id === item.team_id);
      this.taskAllSteps = (team && team.all_steps && team.all_steps.length) ? team.all_steps : [step];
      const idx = this.taskAllSteps.findIndex((s) => s.key === step.key);
      this.taskStepIndex = idx >= 0 ? idx : 0;
      this.taskStep = { ...this.taskAllSteps[this.taskStepIndex] };
      this.taskDraftCache = {};
      this.taskAsking = '';
      this.taskDraft = null;
      this.taskDraftChannel = null;
      this.taskContacts = [];
      this.taskComms = [];
      this.taskEmailTo = '';
      this.taskSmsTo = '';
      this.taskModal = true;
      this.fetchContacts(item.team_id).then((res) => {
        const c = res.contacts || [];
        this.taskContacts = c;
        const e = c.find((x) => x.email); if (e) this.taskEmailTo = e.email;
        const p = c.find((x) => x.phone); if (p) this.taskSmsTo = p.phone;
      }).catch(() => {});
      this.loadTaskComms(item.team_id);
    },
    async loadTaskComms(id) {
      try { this.taskComms = await this.fetchComms(id); } catch (_) { /* non-fatal */ }
      const team = this.teams.find((t) => t.id === id);
      if (team && team.unread_comms > 0) {
        try { await this.markCommsRead(id); this.refresh(); } catch (_) { /* non-fatal */ }
      }
    },
    closeTask() { this.taskModal = false; this.taskStep = null; },
    // Group contacts into [{group, items}] honoring the current step's groups.
    // ── drag-to-reorder table rows (persists a manual order) ──
    onRowDragStart(i) { this.rowDragIndex = i; },
    onRowDragOver(i) { this.rowDragOverIndex = i; },
    onRowDrop(toIdx) {
      const from = this.rowDragIndex;
      this.rowDragOverIndex = null;
      this.rowDragArmed = false;
      if (from === null || from === toIdx) { this.rowDragIndex = null; return; }
      // While a search filter is active sortedTeams is only the visible subset —
      // persisting that as the manual order would silently drop every filtered-out
      // team. Refuse to reorder until the search is cleared.
      if (this.teamSearch && this.teamSearch.trim()) {
        this.rowDragIndex = null;
        this.showToast('Clear the search to reorder rows', 'err');
        return;
      }
      const ordered = this.sortedTeams.map((t) => t.id);
      const [moved] = ordered.splice(from, 1);
      ordered.splice(toIdx, 0, moved);
      this.sortKey = 'manual';
      this.$store.commit('Deployment/SET_MANUAL_ORDER', ordered); // optimistic
      this.reorderTasks(ordered).catch(() => this.showToast('Could not save order', 'err'));
      this.rowDragIndex = null;
    },
    // Open tracking is the pixel only — opened_at is set when the recipient's
    // client loads the tracking pixel embedded in the email.
    readLabel(c) { return c.opened_at ? `Opened ${this.fmtDateTime(c.opened_at)}` : 'Not opened'; },
    readClass(c) { return c.opened_at ? 'is-read' : 'is-unread'; },
    async profileDraft() {
      if (!this.active) return;
      this.profileDrafting = true;
      try {
        const d = await this.draftOutreach({ id: this.active.id, channel: this.profileChannel });
        if (this.profileChannel === 'email') {
          this.profileSubject = d.subject || this.profileSubject;
          this.profileBody = d.email || '';
        } else {
          this.profileBody = d.sms || '';
        }
      } catch (e) {
        this.showToast('Draft failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.profileDrafting = false;
      }
    },
    // Does the SMS "Reply STOP" line get appended? Only for NON-users — i.e. a
    // recipient phone that doesn't match a known team contact (existing Kiey
    // users who consented at signup don't get the disclosure). Approximates the
    // server's global-user check using the loaded contacts for a live preview.
    smsWillAppendStop(to, contacts) {
      if (!to || !to.trim()) return false;
      const norm = (p) => String(p || '').replace(/\D/g, '').slice(-10);
      const t = norm(to);
      if (t.length !== 10) return false;
      const known = (contacts || []).some((c) => c.phone && norm(c.phone) === t);
      return !known;
    },
    openAttach(target) { this.attachTarget = target; this.attachModal = true; },
    // Recipient validation for the compose surfaces.
    isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(e || '').trim()); },
    isValidPhone(p) {
      const s = String(p || '').trim();
      const d = s.replace(/\D/g, '');
      if (d.length === 10) return true; // US 10-digit
      if (d.length === 11 && d[0] === '1') return true; // US with country code
      return /^\+/.test(s) && d.length >= 11 && d.length <= 15; // E.164 international
    },
    recipientValid(to, channel) {
      if (!to || !String(to).trim()) return false;
      return channel === 'sms' ? this.isValidPhone(to) : this.isValidEmail(to);
    },
    // Insert a saved template into the matching compose surface.
    // Map a contact's group (from /contacts) to the invite-template role it can
    // receive. Unknown/non-invitable groups (employees, team contacts, free-typed
    // recipients) return null → all templates stay available.
    groupToRole(group) {
      if (group === 'Vendor') return 'vendor';
      if (group === 'Owner' || group === 'Agent Admin' || group === 'Agent') return 'agent';
      if (group === 'Client') return 'homeowner';
      return null;
    },
    // The role of whoever is in the composer's To field, resolved against the
    // picker's contacts (by email or phone). null when the recipient isn't a
    // known contact (free-typed address, team contact, unrouted reply).
    recipientRole(target) {
      const to = target === 'msg' ? this.msgTo : this.profileTo;
      const contacts = target === 'msg' ? this.msgContacts : this.activeContacts;
      if (!to) return null;
      const key = String(to).toLowerCase().trim();
      const match = (contacts || []).find((c) => (c.email && c.email.toLowerCase() === key) || (c.phone && String(c.phone) === String(to)));
      return match ? this.groupToRole(match.group) : null;
    },
    templatesFor(channel, target) {
      const role = this.recipientRole(target);
      return (this.outreach.templates || []).filter((t) => {
        if ((t.channel || 'email') !== channel) return false;
        // Invite templates carry a role — restrict them to the recipient's role
        // when we know it; custom templates (no role) always show.
        if (t.role && role && t.role !== role) return false;
        return true;
      });
    },
    // Fill the invite-template tokens with this team's real values. Agent/
    // vendor referral links come from the team's /contacts response; the
    // homeowner template has no link token (the agent pastes their own).
    teamNameFor(target) {
      if (target === 'msg') {
        const tm = (this.teams || []).find((x) => x.id === this.msgTeamId);
        return (tm && tm.team_name) || 'the team';
      }
      return (this.active && this.active.team_name) || 'the team';
    },
    // Resolve the recipient currently selected in the To field back to a phone,
    // then fetch that person's short, team-attributed invite link (so the
    // sign-up page pre-fills THEM). Returns '' if no recipient/phone is picked.
    async resolveInviteLink(target, role) {
      const to = target === 'msg' ? this.msgTo : this.profileTo;
      const contacts = target === 'msg' ? this.msgContacts : this.activeContacts;
      const teamId = target === 'msg' ? this.msgTeamId : (this.active && this.active.id);
      if (!teamId || !to) return '';
      const c = (contacts || []).find((x) => x.email === to || x.phone === to);
      const phone = (c && c.phone) || (/^\+?\d[\d\s().-]{6,}$/.test(to) ? to : '');
      if (!phone) {
        this.showToast('Pick a recipient with a phone number first, then re-insert the template', 'err');
        return '';
      }
      try {
        return await this.fetchInviteLink({ id: teamId, phone, role });
      } catch (e) {
        this.showToast('Could not generate the invite link', 'err');
        return '';
      }
    },
    async applyTemplate(id, target) {
      const t = (this.outreach.templates || []).find((x) => x.id === id);
      if (!t) return;
      const teamName = this.teamNameFor(target);
      const fillName = (s) => (s || '').replace(/\{\{\s*team_name\s*\}\}/g, teamName);
      let subject = fillName(t.subject || '');
      let body = fillName(t.body || '');
      // Invite templates carry a role + an {{invite_url}} token resolved per
      // selected recipient.
      if (t.role && /\{\{\s*invite_url\s*\}\}/.test(body)) {
        const link = await this.resolveInviteLink(target, t.role);
        body = body.replace(/\{\{\s*invite_url\s*\}\}/g, link || '[select a recipient above, then re-insert this template]');
      }
      if (target === 'profile') {
        if (t.channel === 'email' && t.subject) this.profileSubject = subject;
        this.profileBody = body;
        this.$nextTick(() => this.autoGrow(this.$refs.profileBodyEl));
      } else if (target === 'msg') {
        if (t.channel === 'email' && t.subject) this.msgSubject = subject;
        this.msgBody = body;
        this.$nextTick(() => this.autoGrow(this.$refs.msgBodyEl));
      }
    },
    // Grow a compose textarea to fit its content (so a pasted-in template isn't
    // hidden behind a scrollbar). Capped so it never eats the whole screen.
    autoGrow(el) {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight + 2, 600)}px`;
    },
    async profileSend() {
      if (!this.active || !this.profileBody.trim()) return;
      if (!this.recipientValid(this.profileTo, this.profileChannel)) { this.showToast(`Enter a valid ${this.profileChannel === 'sms' ? 'phone number' : 'email'}`, 'err'); return; }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Send this ${this.profileChannel} to ${this.profileTo}?`)) return;
      this.profileSending = true;
      try {
        if (this.profileChannel === 'email') {
          await this.sendOutreach({ id: this.active.id, to: this.profileTo, subject: this.profileSubject || 'Message from Kiey Onboarding', body: this.profileBody, attachmentUrls: this.profileAttachUrls });
        } else {
          await this.sendSms({ id: this.active.id, to: this.profileTo, body: this.profileBody });
        }
        this.showToast(`Sent to ${this.profileTo}`, 'ok');
        this.profileBody = '';
        this.profileSubject = '';
        this.profileAttachUrls = [];
        try { this.activeComms = await this.fetchComms(this.active.id); } catch (_) { /* non-fatal */ }
      } catch (e) {
        this.showToast('Send failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.profileSending = false;
      }
    },
    openCommView(c, list) {
      this.commView = c;
      if (Array.isArray(list)) this.commViewList = list;
      this.replyBody = '';
      this.replySubject = c.subject ? `Re: ${c.subject.replace(/^re:\s*/i, '')}` : '';
      this.commModal = true;
    },
    navComm(dir) {
      const i = this.commViewList.findIndex((x) => x.id === this.commView.id);
      const ni = i + dir;
      if (ni >= 0 && ni < this.commViewList.length) this.openCommView(this.commViewList[ni]);
    },
    replyRecipient(c) {
      if (!c) return '';
      const counter = c.direction === 'in' ? c.from : c.to;
      if (c.channel === 'email') { const m = String(counter || '').match(/<([^>]+)>/); return (m ? m[1] : counter || '').trim(); }
      return counter || '';
    },
    async sendReply() {
      const c = this.commView;
      const to = this.replyRecipient(c);
      if (!c || !to || !this.replyBody.trim()) return;
      if (this.replySending) return; // re-entry guard
      const teamId = c.team_id || (this.active && this.active.id);
      if (!teamId) { this.showToast('Missing team for this message.', 'err'); return; }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Send this ${c.channel === 'sms' ? 'text' : 'email'} reply to ${to}?`)) return;
      this.replySending = true;
      try {
        if (c.channel === 'email') {
          await this.sendOutreach({ id: teamId, to, subject: this.replySubject || 'Re:', body: this.replyBody, stepKey: c.step_key });
        } else {
          await this.sendSms({ id: teamId, to, body: this.replyBody, stepKey: c.step_key });
        }
        this.showToast(`Reply sent to ${to}`, 'ok');
        this.commModal = false;
        // Replying handles their message: clear the team's unread so it stops
        // floating to the top, then the outbound stamp drops it to the bottom
        // as done-today (most-urgent → least-urgent ordering).
        try { await this.markCommsRead(teamId); } catch (_) { /* non-fatal */ }
        await this.refresh();
        if (this.active && this.active.id === teamId) { try { this.activeComms = await this.fetchComms(teamId); } catch (_) { /* ignore */ } }
        if (this.messagingModal) this.openMessaging();
      } catch (e) {
        this.showToast('Reply failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.replySending = false;
      }
    },
    async openMessaging() {
      this.messagingModal = true;
      this.allCommsLoading = true;
      try { this.allComms = await this.fetchAllComms(); } catch (_) { this.showToast('Could not load messages', 'err'); } finally { this.allCommsLoading = false; }
      try { this.unroutedComms = await this.fetchUnrouted(); } catch (_) { /* non-fatal */ }
    },
    async openAiActivity() {
      this.aiActivityModal = true;
      this.aiLoading = true;
      try {
        const res = await this.fetchAiActivity();
        this.aiItems = res.items || [];
        this.aiSummary = res.summary || {};
        // Select the newest day (today) by default — date tabs run across the top.
        const first = this.aiByDay[0];
        this.aiActiveDay = first ? first.day : null;
      } catch (_) { this.showToast('Could not load AI activity', 'err'); } finally { this.aiLoading = false; }
    },
    openAiMessage(c) {
      this.aiMessageSel = c;
      this.aiMessageModal = true;
    },
    openOptOuts() {
      this.optOutModal = true;
    },
    async assignUnrouted(c) {
      const teamId = this.assignChoice[c.id];
      if (!teamId) return;
      this.assigning = c.id;
      try {
        await this.assignComm({ commId: c.id, teamId });
        this.unroutedComms = this.unroutedComms.filter((x) => x.id !== c.id);
        this.$delete(this.assignChoice, c.id);
        this.showToast('Reply routed to team', 'ok');
        this.refresh();
        try { this.allComms = await this.fetchAllComms(); } catch (_) { /* non-fatal */ }
      } catch (e) {
        this.showToast('Could not assign: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.assigning = null;
      }
    },
    async dismissUnrouted(c) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this reply? It will be removed from the triage tray.')) return;
      this.dismissing = c.id;
      try {
        await this.dismissComm(c.id);
        this.unroutedComms = this.unroutedComms.filter((x) => x.id !== c.id);
        this.$delete(this.assignChoice, c.id);
        this.showToast('Reply deleted', 'ok');
      } catch (e) {
        this.showToast('Could not delete: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.dismissing = null;
      }
    },
    async openInsights() {
      this.insightsModal = true;
      this.insightsLoading = true;
      try { this.insights = await this.fetchInsights(); } catch (_) { this.showToast('Could not load insights', 'err'); } finally { this.insightsLoading = false; }
    },
    openHistory(teamId) {
      this.historyTeamFilter = typeof teamId === 'string' ? teamId : '';
      this.historyModal = true;
      this.loadHistoryView();
    },
    setHistoryView(v) {
      if (this.historyView === v) return;
      this.historyView = v;
      this.historyTypeFilter = 'all';
      this.loadHistoryView();
    },
    setHistoryDays(d) { this.historyDays = d; this.loadHistoryView(); },
    onHistoryTeamChange() { if (this.historyView === 'account') this.loadUserActivity(); },
    groupByDay(rows) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const todayMs = today.getTime();
      const yestMs = todayMs - 86400000;
      const label = (ms2) => {
        const d = new Date(ms2); d.setHours(0, 0, 0, 0);
        if (d.getTime() === todayMs) return 'Today';
        if (d.getTime() === yestMs) return 'Yesterday';
        const sameYear = d.getFullYear() === today.getFullYear();
        return new Date(ms2).toLocaleDateString(undefined, sameYear
          ? { weekday: 'short', month: 'short', day: 'numeric' }
          : { month: 'short', day: 'numeric', year: 'numeric' });
      };
      const out = [];
      let cur = null;
      rows.forEach((r) => {
        const l = label(r.at);
        if (!cur || cur.label !== l) { cur = { label: l, rows: [] }; out.push(cur); }
        cur.rows.push(r);
      });
      return out;
    },
    historySince() {
      if (this.historyDays === 1) { const t = new Date(); t.setHours(0, 0, 0, 0); return t.getTime(); }
      return this.historyDays > 0 ? Date.now() - this.historyDays * 86400000 : 0;
    },
    loadHistoryView() {
      if (this.historyView === 'account') this.loadUserActivity();
      else this.loadHistory();
    },
    async loadHistory() {
      this.historyLoading = true;
      try {
        this.historyRows = await this.fetchActivity({ since: this.historySince(), limit: 400 });
      } catch (_) {
        this.showToast('Could not load action history', 'err');
        this.historyRows = [];
      } finally { this.historyLoading = false; }
    },
    async loadUserActivity() {
      this.userActivityLoading = true;
      try {
        this.userActivityRows = await this.fetchUserActivity({
          teamId: this.historyTeamFilter || undefined,
          since: this.historySince(),
          limit: 400,
        });
      } catch (_) {
        this.showToast('Could not load account activity', 'err');
        this.userActivityRows = [];
      } finally { this.userActivityLoading = false; }
    },
    categoryIcon(cat) {
      if (cat === 'user') return '👥';
      if (cat === 'vendor') return '🧰';
      if (cat === 'profile') return '🪪';
      if (cat === 'billing') return '💳';
      return '•';
    },
    async contactFromActivity(row) {
      if (!row.team_id) { this.showToast('No deployment team is linked to this account yet', 'err'); return; }
      if (!row.contact_email && !row.contact_phone) { this.showToast('No contact details on file for this user', 'err'); return; }
      this.historyModal = false;
      this.msgChannel = row.contact_email ? 'email' : 'sms';
      this.msgTeamId = row.team_id;
      await this.openMessaging();
      this.msgComposeOpen = true;
      await this.msgPickTeam();
      this.msgTo = this.msgChannel === 'email' ? (row.contact_email || '') : (row.contact_phone || '');
    },
    teamNameById(id) {
      if (!id) return '';
      const t = (this.teams || []).find((x) => x.id === id);
      return (t && t.team_name) || '';
    },
    activityIcon(type) {
      if (type === 'outreach_email') return '✉️';
      if (type === 'outreach_sms') return '💬';
      if (type === 'step') return '✅';
      if (type === 'step_update') return '☑️';
      if (type === 'status') return '🔁';
      if (type === 'counts') return '🔢';
      if (type === 'note') return '📝';
      if (type === 'comm_route') return '📥';
      if (type === 'comm_dismiss') return '🗑️';
      return '•';
    },
    fmtTime(ms) {
      if (!ms) return '';
      return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    },
    async openSettings() {
      this.settingsModal = true;
      try {
        const s = await this.fetchSettings();
        this.settingsForm = {
          signature: s.signature || '',
          templates: Array.isArray(s.templates) ? s.templates.map((t) => ({ ...t })) : [],
          attachments: Array.isArray(s.attachments) ? s.attachments.map((a) => ({ ...a })) : [],
        };
      } catch (_) { this.showToast('Could not load settings', 'err'); }
    },
    async saveSettingsForm() {
      this.savingSettings = true;
      try {
        const saved = await this.saveSettings({
          signature: this.settingsForm.signature,
          templates: this.settingsForm.templates,
          attachments: this.settingsForm.attachments,
        });
        if (saved) {
          this.settingsForm = { signature: saved.signature || '', templates: saved.templates || [], attachments: saved.attachments || [] };
          this.outreach = { signature: saved.signature || '', templates: saved.templates || [], attachments: saved.attachments || [] };
        }
        this.showToast('Settings saved', 'ok');
        this.settingsModal = false;
      } catch (e) {
        this.showToast('Save failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.savingSettings = false;
      }
    },
    async uploadAttachment(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      e.target.value = '';
      this.uploadingAtt = true;
      try {
        const ref = storage.ref(`deployment_attachments/${Date.now()}_${file.name}`);
        await ref.put(file);
        const url = await ref.getDownloadURL();
        this.settingsForm.attachments.push({ id: 'att_' + Date.now(), name: file.name, url, content_type: file.type || '' });
        this.showToast('Uploaded — remember to Save settings', 'ok');
      } catch (err) {
        this.showToast('Upload failed: ' + (err.message || err), 'err');
      } finally {
        this.uploadingAtt = false;
      }
    },
    async msgPickTeam() {
      this.msgTo = '';
      this.msgContacts = [];
      if (!this.msgTeamId) return;
      try {
        const res = await this.fetchContacts(this.msgTeamId);
        this.msgContacts = res.contacts || [];
      } catch (_) { /* non-fatal */ }
    },
    async msgSend() {
      if (!this.msgTeamId || !this.msgBody.trim()) return;
      if (this.msgSending) return; // re-entry guard
      if (!this.recipientValid(this.msgTo, this.msgChannel)) { this.showToast(`Enter a valid ${this.msgChannel === 'sms' ? 'phone number' : 'email'}`, 'err'); return; }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Send this ${this.msgChannel === 'sms' ? 'SMS' : 'email'} to ${this.msgTo}?`)) return;
      this.msgSending = true;
      try {
        if (this.msgChannel === 'email') {
          await this.sendOutreach({ id: this.msgTeamId, to: this.msgTo, subject: this.msgSubject || 'Message from Kiey Onboarding', body: this.msgBody, attachmentUrls: this.msgAttachUrls });
        } else {
          await this.sendSms({ id: this.msgTeamId, to: this.msgTo, body: this.msgBody });
        }
        this.showToast(`Sent to ${this.msgTo}`, 'ok');
        this.msgBody = ''; this.msgSubject = ''; this.msgAttachUrls = []; this.msgComposeOpen = false;
        this.refresh();
        try { this.allComms = await this.fetchAllComms(); } catch (_) { /* non-fatal */ }
      } catch (e) {
        this.showToast('Send failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.msgSending = false;
      }
    },
    gotoStep(i) {
      if (i < 0 || i >= this.taskAllSteps.length) return;
      this.taskStepIndex = i;
      this.taskStep = { ...this.taskAllSteps[i] };
      this.taskAsking = '';
      // Restore any draft we already generated for this step.
      const cached = this.taskDraftCache[this.taskStep.key];
      if (cached) { this.taskDraft = cached.draft; this.taskDraftChannel = cached.channel; } else { this.taskDraft = null; this.taskDraftChannel = null; }
    },
    async sendSmsTo(teamId, body, to) {
      if (!to) { this.showToast('Pick a phone number first.', 'err'); return; }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Text ${to} from the Kiey number?`)) return;
      this.smsSending = true;
      try {
        await this.sendSms({ id: teamId, to, body, stepKey: this.taskStep && this.taskStep.key });
        this.showToast(`SMS sent to ${to}`, 'ok');
        this.loadTaskComms(teamId);
        if (this.active && this.active.id === teamId) {
          try { this.updates = await this.fetchUpdates(teamId); } catch (_) { /* non-fatal */ }
        }
      } catch (e) {
        this.showToast('SMS failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.smsSending = false;
      }
    },
    currentSubStep() {
      const subs = (this.taskStep && this.taskStep.sub_steps) || [];
      const done = (this.taskStep && this.taskStep.sub_done) || [];
      let i = subs.findIndex((s, idx) => !done.includes(idx));
      if (i === -1) i = subs.length - 1; // all done → last
      return subs[i] || '';
    },
    async draftChannel(channel) {
      this.taskDraftChannel = channel;
      this.taskDraftLoading = true;
      try {
        this.taskDraft = await this.draftOutreach({
          id: this.taskTeamId, channel, stepKey: this.taskStep.key, subStep: this.currentSubStep(),
        });
        // Cache so navigating away and back restores it.
        this.$set(this.taskDraftCache, this.taskStep.key, { channel, draft: this.taskDraft });
      } catch (e) {
        this.taskDraft = null;
        this.showToast('Draft failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.taskDraftLoading = false;
      }
    },
    async confirmStepDone() {
      this.taskBusy = true;
      try {
        await this.setStepState({ id: this.taskTeamId, stepKey: this.taskStep.key, state: 'pending' });
        this.taskStep.state = 'pending';
        this.taskAsking = '';
        this.showToast('Marked pending — awaiting count confirmation', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Could not update: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.taskBusy = false;
      }
    },
    async completeStep() {
      this.taskBusy = true;
      try {
        await this.setStepState({ id: this.taskTeamId, stepKey: this.taskStep.key, state: 'done' });
        this.showToast('Step complete', 'ok');
        this.closeTask();
        this.refresh();
      } catch (e) {
        this.showToast('Could not complete: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.taskBusy = false;
      }
    },
    // Reopen a step that was marked done/pending — sets it back to 'todo' so a
    // mistakenly-completed step can be corrected. (Metric-driven steps that are
    // genuinely complete by live count will re-show complete after refresh.)
    async reopenStep() {
      this.taskBusy = true;
      try {
        await this.setStepState({ id: this.taskTeamId, stepKey: this.taskStep.key, state: 'todo' });
        this.$set(this.taskStep, 'state', 'todo');
        this.$set(this.taskStep, 'complete', false);
        this.taskAsking = '';
        this.showToast('Step reopened', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Could not reopen: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.taskBusy = false;
      }
    },
    async logFollowup(channel) {
      this.taskBusy = true;
      try {
        await this.setStepState({ id: this.taskTeamId, stepKey: this.taskStep.key, follow_up: { channel } });
        this.taskAsking = '';
        this.showToast(`${channel} follow-up logged`, 'ok');
        this.draftChannel(channel); // draft the script to send
        this.refresh();
      } catch (e) {
        this.showToast('Could not log follow-up', 'err');
      } finally {
        this.taskBusy = false;
      }
    },

    // Silent background poll: refresh the board (envelopes) + any open thread.
    // True when the operator has unsaved edits or an in-progress compose/draft —
    // the poll must not refetch and reassign state underneath them.
    isMidEdit() {
      const dirty = (s) => !!(s && String(s).trim());
      return this.saveState === 'saving'
        || !!this.autoSaveTimer
        || dirty(this.profileBody) || dirty(this.profileSubject)
        || dirty(this.replyBody) || dirty(this.msgBody);
    },
    async pollUpdates() {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (this.commModal || this.fieldModal || this.stepsModal || this.createModal) return; // don't disrupt active editing
      if (this.isMidEdit()) return; // don't clobber an unsaved edit / open compose mid-keystroke
      try {
        await this.fetchTeams({ silent: true });
        if (this.active) {
          const fresh = this.teams.find((t) => t.id === this.active.id);
          if (fresh) this.active = fresh;
          try { this.activeComms = await this.fetchComms(this.active.id); } catch (_) { /* non-fatal */ }
        }
        if (this.taskModal && this.taskTeamId) {
          try { this.taskComms = await this.fetchComms(this.taskTeamId); } catch (_) { /* non-fatal */ }
        }
      } catch (_) { /* silent */ }
    },
    async refresh() {
      this.loading = true;
      this.errorMsg = null;
      try {
        await this.fetchTeams({ force: true });
        if (this.active) {
          const fresh = this.teams.find((t) => t.id === this.active.id);
          if (fresh) this.active = fresh;
          // Refetch the open thread too so a manual refresh updates the history.
          try { this.activeComms = await this.fetchComms(this.active.id); } catch (_) { /* non-fatal */ }
        }
      } catch (e) {
        this.errorMsg = 'Could not load teams: ' + (e.response?.data?.errors?.join(', ') || e.message);
      } finally {
        this.loading = false;
      }
    },

    // Daily worklist status is set EXPLICITLY when the operator closes the team
    // profile ("Are you done with this client today?"). It's timestamped so it
    // resets each day. 'done' → sinks to the bottom + "Done today"; 'in_progress'
    // → stays put + "In Progress" tag.
    _dailyStatusToday(t) {
      if (!t || !t.daily_status || !t.daily_status_at) return '';
      const d = new Date(t.daily_status_at);
      const now = new Date();
      const isToday = d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
      return isToday ? t.daily_status : '';
    },
    doneToday(t) { return this._dailyStatusToday(t) === 'done'; },
    inProgressToday(t) { return this._dailyStatusToday(t) === 'in_progress'; },

    // Manual Refresh-button handler: re-pulls everything and confirms it ran.
    // The plain refresh() is silent (used after saves / on mount); this wrapper
    // gives the operator visible feedback since the data is often unchanged.
    async manualRefresh() {
      await this.refresh();
      if (!this.errorMsg) this.showToast('Up to date', 'ok');
    },

    setSort(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      else { this.sortKey = key; this.sortDir = 'asc'; }
    },
    sortIndicator(key) {
      if (this.sortKey !== key) return '↕';
      return this.sortDir === 'asc' ? '▲' : '▼';
    },

    stageClass(stage) {
      return 'stg-' + String(stage || 'Prospect').toLowerCase().replace(/\s+/g, '-');
    },
    // Deterministic per-team color (same id → same color everywhere).
    teamColor(id) {
      return this.teamColorMap[id] || '#7f8c8d';
    },
    stepColor(step) {
      const palette = ['#2980b9', '#27ae60', '#e67e22', '#8e44ad', '#16a085', '#c0392b', '#2c3e50'];
      const n = step && step.number ? Number(step.number) : 0;
      return n > 0 ? palette[(n - 1) % palette.length] : '#7f8c8d';
    },
    stepChipStyle(step) {
      const c = this.stepColor(step);
      return { borderColor: c, color: c };
    },
    pct(a, b) { return b > 0 ? Math.round((Number(a) || 0) / b * 100) : 0; },
    // A step's completion %: the share of its sub-steps checked off (so 4
    // sub-steps move in 25% increments). Steps with no sub-steps are 0/100 by
    // their done state.
    stepPct(s) {
      if (!s) return 0;
      const total = (s.sub_steps && s.sub_steps.length) || 0;
      if (!total) return (s.complete || s.state === 'done') ? 100 : 0;
      const done = (s.sub_done || []).filter((x) => x >= 0 && x < total).length;
      return Math.round((done / total) * 100);
    },
    stepBadge(s) {
      if (!s) return '✓';
      let badge = String(s.number || '');
      if (s.sub_steps && s.sub_steps.length) {
        const done = s.sub_done || [];
        let cur = 0;
        while (cur < s.sub_steps.length && done.includes(cur)) cur += 1;
        if (cur >= s.sub_steps.length) cur = s.sub_steps.length - 1;
        badge += String.fromCharCode(97 + cur);
      }
      return badge;
    },
    // Which step a table row is currently showing (defaults to the first
    // incomplete step) + back/forward across the team's full playbook.
    stepList(t) { return (t.all_steps && t.all_steps.length) ? t.all_steps : (t.next_steps || []); },
    defaultStepIdx(t) {
      const list = this.stepList(t);
      const i = list.findIndex((s) => !s.complete);
      return i >= 0 ? i : Math.max(0, list.length - 1);
    },
    shownStepIdx(t) {
      const v = this.rowStepIdx[t.id];
      return (v === undefined || v === null) ? this.defaultStepIdx(t) : v;
    },
    shownStep(t) { return this.stepList(t)[this.shownStepIdx(t)] || null; },
    rowNav(t, delta) {
      const list = this.stepList(t);
      const next = Math.min(list.length - 1, Math.max(0, this.shownStepIdx(t) + delta));
      this.$set(this.rowStepIdx, t.id, next);
    },
    openField(t, type) {
      this.fieldTeam = t;
      this.fieldType = type;
      this.fieldSaving = false;
      this.fieldNotes = [];
      if (type === 'counts') {
        this.fieldForm = {
          agents_active: t.agents_active, agents_target: t.agents_target,
          vendors_active: t.vendors_active, vendors_total: t.vendors_total,
          homeowners_active: t.homeowners_active, homeowners_total: t.homeowners_total,
        };
      } else if (type === 'registered') {
        this.fieldForm = {
          registered_date: this.toInputDate(t.registered_date),
          intro_demo_done: !!t.intro_demo_done,
          intro_demo_date: this.toInputDate(t.intro_demo_date),
        };
      } else if (type === 'followup') {
        this.fieldForm = { title: '', due_date: '' };
      } else if (type === 'note') {
        this.fieldForm = { text: '' };
        this.fetchUpdates(t.id).then((u) => { this.fieldNotes = u; }).catch(() => {});
      }
      this.fieldModal = true;
    },
    async saveField() {
      if (!this.fieldTeam) return;
      const id = this.fieldTeam.id;
      this.fieldSaving = true;
      try {
        if (this.fieldType === 'counts') {
          await this.updateTeam({ id, fields: { ...this.fieldForm } });
        } else if (this.fieldType === 'registered') {
          await this.updateTeam({
            id,
            fields: {
              registered_date: this.fieldForm.registered_date || null,
              intro_demo_done: !!this.fieldForm.intro_demo_done,
              intro_demo_date: this.fieldForm.intro_demo_date || null,
            },
          });
        } else if (this.fieldType === 'followup') {
          if (this.fieldForm.title.trim()) {
            await this.addFollowup({ id, title: this.fieldForm.title.trim(), due_date: this.fieldForm.due_date || null });
          }
        } else if (this.fieldType === 'note') {
          if (this.fieldForm.text.trim()) await this.addUpdate({ id, text: this.fieldForm.text.trim() });
        }
        this.fieldModal = false;
        this.showToast('Saved', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Save failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.fieldSaving = false;
      }
    },
    teamNextFuTitle(t) {
      const fus = this.followups.filter((f) => f.team_id === t.id);
      if (!fus.length) return '';
      fus.sort((a, b) => (a.due_date || Infinity) - (b.due_date || Infinity));
      return fus[0].title;
    },
    legPct(leg) {
      const a = Number(this.form[leg + '_active']) || 0;
      if (leg === 'agents') {
        const tgt = Number(this.form.agents_target) || 0;
        return tgt > 0 ? Math.min(100, Math.round((a / tgt) * 100)) : 0;
      }
      if (leg === 'vendors') return Math.min(100, Math.round((a / VENDOR_TARGET) * 100));
      return Math.min(100, Math.round((a / HOMEOWNER_TARGET) * 100));
    },

    // ── date helpers ──
    toMillis(v) {
      if (v == null) return null;
      if (typeof v === 'number') return v;
      if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? null : t; }
      if (typeof v._seconds === 'number') return v._seconds * 1000;
      if (typeof v.seconds === 'number') return v.seconds * 1000;
      return null;
    },
    fmtDate(v) { const ms = this.toMillis(v); return ms ? new Date(ms).toLocaleDateString() : '—'; },
    fmtDateTime(v) { const ms = this.toMillis(v); return ms ? new Date(ms).toLocaleString() : ''; },
    // Concise duration from seconds: 45s · 12m · 3h 20m · 2d 4h · — when no data.
    fmtDur(sec) {
      if (!sec || sec <= 0) return '—';
      if (sec < 60) return `${sec}s`;
      const m = Math.round(sec / 60);
      if (m < 60) return `${m}m`;
      const h = Math.floor(m / 60);
      const rm = m % 60;
      if (h < 24) return rm ? `${h}h ${rm}m` : `${h}h`;
      const d = Math.floor(h / 24);
      const rh = h % 24;
      return rh ? `${d}d ${rh}h` : `${d}d`;
    },
    toInputDate(v) {
      const ms = this.toMillis(v); if (!ms) return '';
      const d = new Date(ms);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },

    // ── AI outreach ──
    openOutreach(teamId, teamName) {
      this.outreachTeamId = teamId;
      this.outreachTeamName = teamName;
      this.outreachData = null;
      this.outreachModal = true;
      this.loadOutreach();
    },
    async loadOutreach() {
      if (!this.outreachTeamId) return;
      this.outreachLoading = true;
      try {
        this.outreachData = await this.draftOutreach({ id: this.outreachTeamId });
      } catch (e) {
        this.outreachData = null;
        this.showToast('Outreach draft failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.outreachLoading = false;
      }
    },
    closeOutreach() { this.outreachModal = false; this.outreachData = null; this.outreachTeamId = null; },
    copyText(t) {
      if (!t) return;
      try { navigator.clipboard.writeText(t); this.showToast('Copied', 'ok'); } catch (_) { /* blocked */ }
    },

    // ── profile ──
    hydrateForm(t) {
      this.hydrating = true;
      this.form = {
        ...emptyForm(),
        ...t,
        registered_date: this.toInputDate(t.registered_date),
        intro_demo_date: this.toInputDate(t.intro_demo_date),
        next_meeting_date: this.toInputDate(t.next_meeting_date),
      };
      this.$nextTick(() => { this.hydrating = false; });
    },
    async openTeam(t) {
      this.active = t;
      this.hydrateForm(t);
      this.updates = [];
      this.newUpdate = '';
      this.editingNoteId = null;
      this.tenantQuery = '';
      this.tenantResults = [];
      this.saveState = 'idle';
      this.activeComms = [];
      this.activeContacts = [];
      this.profileChannel = 'email';
      this.profileTo = '';
      this.profileSubject = '';
      this.profileBody = '';
      window.scrollTo(0, 0);
      // Guard against a slow earlier fetch landing after the operator has
      // already switched to a different team (would overwrite the new team's
      // data). Only apply each result if this team is still the active one.
      const stillActive = () => this.active && this.active.id === t.id;
      try { const u = await this.fetchUpdates(t.id); if (stillActive()) this.updates = u; } catch (_) { /* non-fatal */ }
      try { const c = await this.fetchComms(t.id); if (stillActive()) this.activeComms = c; } catch (_) { /* non-fatal */ }
      try { const k = await this.fetchContacts(t.id); if (stillActive()) this.activeContacts = k.contacts || []; } catch (_) { this.showToast('Could not load contacts for the recipient picker', 'err'); }
      if (t.unread_comms > 0) {
        try { await this.markCommsRead(t.id); this.refresh(); } catch (_) { /* non-fatal */ }
      }
    },
    openTeamById(id) {
      const t = this.teams.find((x) => x.id === id);
      if (t) this.openTeam(t);
    },
    back() { this.active = null; },
    // Row chip toggle: flip a team's daily worklist status (In progress ↔ Done
    // today) inline — no modal. 'done' sinks it to the bottom on refresh.
    async setRowStatus(t, status) {
      try { await this.setDailyStatus({ id: t.id, status }); await this.refresh(); }
      catch (_) { this.showToast('Could not save status', 'err'); }
    },
    // Closing just closes — no "are you done?" prompt. Status is managed via the
    // clickable In Progress / Done chip on each row.
    askCloseStatus() { this.back(); },
    async finishClose(status) {
      const id = this.active && this.active.id;
      this.closePromptModal = false;
      if (id && status) {
        try { await this.setDailyStatus({ id, status }); } catch (_) { this.showToast('Could not save status', 'err'); }
      }
      this.back();
      this.refresh(); // re-sort (done → bottom)
    },

    buildPayload() {
      const f = this.form;
      return {
        team_name: f.team_name, status: f.status, tenant_id: f.tenant_id || null,
        assigned_to: f.assigned_to || '',
        registered_date: f.registered_date || null,
        intro_demo_date: f.intro_demo_date || null, intro_demo_done: !!f.intro_demo_done,
        admin_name: f.admin_name, admin_email: f.admin_email, admin_phone: f.admin_phone,
        agents_active: f.agents_active, agents_total: f.agents_total, agents_target: f.agents_target,
        vendors_active: f.vendors_active, vendors_total: f.vendors_total,
        homeowners_active: f.homeowners_active, homeowners_total: f.homeowners_total,
        marketing_packages: f.marketing_packages,
        next_meeting_date: f.next_meeting_date || null, next_meeting_note: f.next_meeting_note,
      };
    },
    scheduleAutoSave() {
      this.saveState = 'saving';
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = setTimeout(() => this.autoSave(), 800);
    },
    async autoSave() {
      if (!this.active) return;
      try {
        const team = await this.updateTeam({ id: this.active.id, fields: this.buildPayload() });
        // Refresh stage/alerts/counts_source from the saved team WITHOUT
        // re-hydrating the form (don't clobber what the user is editing).
        this.active = team;
        this.saveState = 'saved';
        clearTimeout(this._savedTimer);
        this._savedTimer = setTimeout(() => { if (this.saveState === 'saved') this.saveState = 'idle'; }, 2000);
      } catch (e) {
        this.saveState = 'idle';
        this.showToast('Auto-save failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      }
    },

    alertAction(a) {
      switch (a.key) {
        case 'intro_demo': return { label: 'Mark demo done', fields: { intro_demo_done: true } };
        case 'meeting_overdue': return { label: 'Clear meeting', fields: { next_meeting_date: null, next_meeting_note: '' } };
        case 'stale': return { label: 'Add a note', focusUpdate: true };
        default:
          if (SYNC_KEYS.includes(a.key)) return this.form.tenant_id ? { label: 'Sync counts', sync: true } : null;
          return null;
      }
    },
    async resolveAlert(a) {
      const action = this.alertAction(a);
      if (!action) return;
      if (action.focusUpdate) {
        this.$nextTick(() => { if (this.$refs.updateInput) this.$refs.updateInput.focus(); });
        return;
      }
      this.resolvingKey = a.key;
      try {
        if (action.sync) {
          await this.doSync(true);
        } else if (action.fields) {
          this.hydrating = true;
          const team = await this.updateTeam({ id: this.active.id, fields: action.fields });
          this.active = team;
          this.hydrateForm(team);
          this.showToast('Done', 'ok');
        }
      } catch (e) {
        this.showToast('Could not complete: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.resolvingKey = null;
      }
    },

    async doSync(silent) {
      if (!this.active) return;
      this.syncing = true;
      try {
        const team = await this.syncCounts(this.active.id);
        this.active = team;
        this.hydrateForm(team);
        if (!silent) this.showToast('Counts synced from tenant', 'ok');
      } catch (e) {
        this.showToast('Sync failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.syncing = false;
      }
    },

    onTenantSearch() {
      clearTimeout(this.tenantSearchTimer);
      const q = this.tenantQuery.trim();
      if (!q) { this.tenantResults = []; return; }
      this.tenantSearchTimer = setTimeout(async () => {
        try { this.tenantResults = await this.searchTenants(q); } catch (_) { this.tenantResults = []; }
      }, 300);
    },
    linkTenant(tr) { this.form.tenant_id = tr.tenant_id; this.tenantResults = []; this.tenantQuery = ''; },

    // ── notes (timestamped, editable) ──
    async postUpdate() {
      const text = this.newUpdate.trim();
      if (!text || !this.active) return;
      this.postingUpdate = true;
      try {
        const u = await this.addUpdate({ id: this.active.id, text });
        if (u) this.updates.unshift(u);
        this.newUpdate = '';
        // Prompt to capture a follow-up task off the back of this note.
        this.followupTitle = '';
        this.followupDate = '';
        this.followupPrompt = true;
        this.showToast('Note added', 'ok');
        // Re-pull so the table's latest-note column reflects it immediately.
        await this.refresh();
      } catch (e) {
        this.showToast('Could not add note', 'err');
      } finally {
        this.postingUpdate = false;
      }
    },
    startEditNote(u) { this.editingNoteId = u.id; this.editNoteText = u.text; },
    async saveEditNote(u) {
      const text = this.editNoteText.trim();
      if (!text) return;
      try {
        const updated = await this.editNote({ id: this.active.id, noteId: u.id, text });
        const idx = this.updates.findIndex((x) => x.id === u.id);
        if (idx !== -1 && updated) this.$set(this.updates, idx, { ...this.updates[idx], ...updated });
        this.editingNoteId = null;
        this.showToast('Note updated', 'ok');
      } catch (e) {
        this.showToast('Could not update note', 'err');
      }
    },
    async removeNote(u) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this note?')) return;
      try {
        await this.deleteNote({ id: this.active.id, noteId: u.id });
        this.updates = this.updates.filter((x) => x.id !== u.id);
        this.showToast('Note deleted', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Could not delete note', 'err');
      }
    },

    async confirmDelete() {
      if (!this.active) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete "${this.active.team_name}" and all its notes + next actions? This cannot be undone.`)) return;
      try {
        await this.deleteTeam(this.active.id);
        this.showToast('Team deleted', 'ok');
        this.back();
      } catch (e) {
        this.showToast('Delete failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      }
    },
    async deleteRow(t) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete "${t.team_name}"? This removes the team, its notes, and its next actions.`)) return;
      // If this team's profile is open, close it so we don't leave a stale modal.
      if (this.active && this.active.id === t.id) this.back();
      try {
        await this.deleteTeam(t.id);
        this.showToast(`Deleted ${t.team_name}`, 'ok');
      } catch (e) {
        this.showToast('Delete failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      }
    },

    openCreate() {
      this.createForm = { team_name: '', status: 'Prospect', agents_target: 0 };
      this.createModal = true;
    },
    async doCreate() {
      const name = this.createForm.team_name.trim();
      if (!name) return;
      this.creating = true;
      try {
        await this.createTeam({ team_name: name, status: this.createForm.status, agents_target: this.createForm.agents_target || 0 });
        this.createModal = false;
        this.showToast('Team added', 'ok');
        this.refresh();
      } catch (e) {
        this.showToast('Create failed: ' + (e.response?.data?.errors?.join(', ') || e.message), 'err');
      } finally {
        this.creating = false;
      }
    },

    showToast(msg, type) {
      this.toast = msg;
      this.toastType = type === 'err' ? 'err' : 'ok';
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.toast = ''; }, 3000);
    },
  },
};
</script>

<style scoped>
.dt-wrap { padding: 24px; max-width: 1200px; margin: 0 auto; color: #1c2536; font-family: inherit; }
.dt-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.dt-title { font-size: 26px; font-weight: 700; color: #061D47; margin: 0; }
.dt-sub { color: #6b7280; font-size: 14px; margin: 4px 0 0; }
.dt-header-actions { display: flex; gap: 10px; }

.dt-btn { border: none; border-radius: 8px; padding: 9px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: 0.15s; }
.dt-btn:disabled { opacity: 0.6; cursor: default; }
.dt-btn-sm { padding: 6px 12px; font-size: 13px; white-space: nowrap; }
.dt-btn-primary { background: #061D47; color: #fff; }
.dt-btn-primary:hover:not(:disabled) { background: #0a2a63; }
.dt-btn-ghost { background: #eef1f6; color: #1c2536; }
.dt-btn-ghost:hover:not(:disabled) { background: #e2e7ef; }
.dt-btn-danger-ghost { background: transparent; color: #c0392b; }
.dt-btn-danger-ghost:hover { background: #fdecea; }

.dt-error { background: #fdecea; color: #c0392b; padding: 10px 14px; border-radius: 8px; margin: 12px 0; font-size: 14px; }

.dt-pipeline { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 18px; }
.dt-pipeline-stat { font-size: 13px; font-weight: 600; color: #d35400; margin-left: auto; }
.dt-edit-order { margin-left: auto; }

/* Action-steps editor */
.dt-steps-edit { list-style: none; margin: 0 0 10px; padding: 0; display: flex; flex-direction: column; gap: 10px; max-height: 60vh; overflow-y: auto; }
.dt-step-edit { border: 1px solid #e6e9ef; border-radius: 10px; padding: 12px; background: #fafbfc; }
.dt-step-edit.dragover { border-color: #1b5fd0; }
.dt-step-edit-head { display: flex; align-items: center; gap: 8px; }
.dt-step-metric { max-width: 130px; }
.dt-step-desc-input { margin-top: 8px; }
.dt-substeps-edit { margin-top: 10px; padding-left: 4px; }
.dt-substep-row { display: flex; align-items: center; gap: 6px; margin: 4px 0; border: 1px solid transparent; border-radius: 7px; }
.dt-substep-row.dragover { border-color: #1b5fd0; }
.dt-sub-drag { font-size: 13px; }
.dt-substep-num { font-size: 12px; color: #9aa3b2; min-width: 16px; }
.dt-add-step { margin-bottom: 10px; }

/* Sub-step checklist in the task modal */
.dt-substeps { border-top: 1px solid #eef0f4; padding-top: 14px; }
.dt-substeps .dt-flabel { margin-bottom: 10px; }
/* Neutralize the global label{} !important rule on these checkbox labels. */
.dt-substep-check { display: flex !important; align-items: center; gap: 10px; font-size: 14px !important; font-weight: 500 !important; padding: 6px 0; cursor: pointer; transform: none !important; margin: 0 !important; max-width: 100% !important; line-height: 1.4 !important; color: #1c2536 !important; }
.dt-substep-check input { width: auto; flex: 0 0 auto; }
.dt-sub-letter { font-weight: 700; color: #8a93a3; flex: 0 0 auto; }
.dt-sub-done { color: #9aa3b2; text-decoration: line-through; }

.dt-stage-pill { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }
.stg-prospect { background: #eceff3; color: #5b6472; }
.stg-onboarding-agents { background: #e7f0ff; color: #1b5fd0; }
.stg-recruiting-vendors { background: #fff2dd; color: #b9770e; }
.stg-inviting-homeowners { background: #ede4ff; color: #6b34c9; }
.stg-live { background: #def7e6; color: #1d8a4a; }

.dt-card { background: #fff; border: 1px solid #e6e9ef; border-radius: 12px; padding: 20px; margin-bottom: 18px; }
.dt-section-title { font-size: 16px; font-weight: 700; color: #061D47; margin: 0 0 14px; display: flex; align-items: center; gap: 8px; }
.dt-count { font-size: 12px; font-weight: 700; color: #fff; background: #061D47; border-radius: 12px; padding: 1px 9px; }
.dt-hint { font-size: 11px; font-weight: 500; color: #9aa3b2; margin-left: auto; text-transform: none; letter-spacing: 0; }

/* Work board (bucketed) */
.dt-bucket { margin-bottom: 16px; }
.dt-bucket:last-child { margin-bottom: 0; }
.dt-bucket-head { display: flex; align-items: center; gap: 8px; margin: 4px 0 8px; }
.dt-bucket-chip { font-size: 12px; font-weight: 700; padding: 3px 12px; border-radius: 16px; white-space: nowrap; display: inline-block; }
.bk-today { background: #fdecea; color: #c0392b; }
.bk-this-week { background: #fff2dd; color: #b9770e; }
.bk-next-2-weeks { background: #e7f0ff; color: #1b5fd0; }
.bk-later { background: #eceff3; color: #5b6472; }
.dt-board-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.dt-board-row { display: flex; align-items: center; gap: 12px; padding: 11px 12px; border: 1px solid #eceef2; border-left-width: 4px; border-radius: 8px; background: #fff; cursor: pointer; transition: 0.12s; }
.dt-board-row:hover { background: #f8f9fb; }
.dt-board-main { flex: 1; min-width: 0; }
.dt-board-title { font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.dt-board-fu { font-size: 12px; color: #b9770e; margin-top: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dt-board-note { font-size: 12px; color: #9aa3b2; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Row drag handle + table chips */
.dt-drag { cursor: grab; color: #c2c8d2; font-size: 14px; user-select: none; }
.dt-drag:active { cursor: grabbing; }
.dt-drag-off { cursor: not-allowed; opacity: 0.35; }
.dt-task-del { border: none; background: transparent; color: #b0b7c3; font-size: 13px; cursor: pointer; padding: 4px 6px; border-radius: 6px; }
.dt-task-del:hover { color: #c0392b; background: #fdecea; }
.dt-rank { font-size: 13px; font-weight: 700; color: #b0b7c3; min-width: 18px; text-align: center; }
.dt-step-chips { display: flex; gap: 6px; flex-wrap: wrap; max-width: 360px; justify-content: flex-end; }
.dt-step-chip { border: 1px solid #d6dbe4; background: #f7f8fa; color: #1c2536; font-size: 12px; font-weight: 600; border-radius: 16px; padding: 5px 11px; cursor: pointer; white-space: nowrap; display: inline-flex; align-items: center; gap: 5px; min-width: 0; max-width: 100%; }
.dt-chip-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.dt-step-chip:hover { border-color: #1b5fd0; color: #1b5fd0; }
.dt-step-chip.pending { background: #fff5e6; border-color: #e7b766; }
.dt-chip-pending { font-size: 10px; font-weight: 700; color: #b9770e; text-transform: uppercase; }
.dt-chip-fu { font-size: 10px; color: #9aa3b2; }
.dt-timeinstep { font-weight: 700; color: #061D47; }
.dt-team-reg { font-size: 11px; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dt-step-meta { font-size: 11px; margin-top: 4px; }
.dt-next-cell { text-align: center; }
.dt-teams-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
.dt-input.dt-team-search { max-width: 190px; padding: 5px 10px; font-size: 13px; }
.dt-input.dt-assignee-filter { max-width: 130px; margin-left: 8px; padding: 5px 8px; font-size: 13px; }
.dt-assignee { color: #6b7280; }
.dt-settings-btn { margin-left: auto; }
.dt-sig-preview { font-size: 12px; color: #6b7280; font-style: italic; white-space: pre-wrap; background: #f7f8fa; border: 1px dashed #d6dbe4; border-radius: 7px; padding: 7px 10px; margin-top: 6px; }
.dt-sig-preview-label { font-size: 11px; font-weight: 700; color: #9aa3b2; text-transform: uppercase; letter-spacing: 0.3px; }

/* Unrouted-reply triage */
.dt-unrouted { background: #fff8e6; border: 1px solid #f0d98a; border-radius: 8px; padding: 10px 12px; margin-bottom: 12px; }
.dt-unrouted-title { font-size: 13px; font-weight: 700; color: #8a6d1f; margin-bottom: 8px; }
.dt-unrouted-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-top: 1px solid #f0e3b8; }
.dt-unrouted-main { flex: 1; min-width: 0; }
.dt-unrouted-from { font-size: 12px; font-weight: 600; color: #1c2536; }
.dt-unrouted-preview { font-size: 12px; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dt-unrouted-select { max-width: 180px; }
.dt-unrouted-delete { color: #b42318; }
.dt-unrouted-delete:hover { background: #fef3f2; }

/* Action History */
.dt-hist-inline { margin-left: 10px; font-weight: 500; }
.dt-hist-views { display: inline-flex; gap: 0; border: 1px solid #d6dbe4; border-radius: 8px; overflow: hidden; margin: 6px 0 4px; }
.dt-hist-view { border: none; background: #fff; color: #475467; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.dt-hist-view + .dt-hist-view { border-left: 1px solid #d6dbe4; }
.dt-hist-view.active { background: #061d47; color: #fff; }
.dt-hist-role { color: #98a2b3; font-weight: 500; }
.dt-hist-contact { flex: 0 0 auto; color: #1b5fd0; }
.dt-hist-contact:hover:not(:disabled) { background: #eef4ff; }
.dt-hist-contact:disabled { color: #c2c8d0; cursor: not-allowed; }
.dt-hist-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 10px 0 14px; }
.dt-hist-team { max-width: 220px; }
.dt-hist-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.dt-hist-chip { border: 1px solid #d6dbe4; background: #fff; color: #475467; border-radius: 999px; padding: 4px 12px; font-size: 12px; font-weight: 600; cursor: pointer; }
.dt-hist-chip:hover { background: #f3f6fb; }
.dt-hist-chip.active { background: #061d47; border-color: #061d47; color: #fff; }
.dt-hist-feed { max-height: 60vh; overflow-y: auto; }
.dt-hist-day { position: sticky; top: 0; background: #fff; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #98a2b3; padding: 12px 2px 5px; border-bottom: 1px solid #eef0f4; }
.dt-hist-row { display: flex; align-items: baseline; gap: 9px; padding: 7px 2px; font-size: 13px; border-bottom: 1px solid #f4f5f8; }
.dt-hist-time { flex: 0 0 64px; color: #98a2b3; font-size: 12px; font-variant-numeric: tabular-nums; }
.dt-hist-icon { flex: 0 0 18px; }
.dt-hist-summary { flex: 1; min-width: 0; color: #1c2536; }
.dt-hist-detail { color: #98a2b3; }
.dt-hist-team-tag { flex: 0 0 auto; font-size: 11px; font-weight: 600; color: #1b5fd0; background: #eef4ff; border-radius: 5px; padding: 1px 7px; }
.dt-hist-by { flex: 0 0 auto; font-size: 11px; color: #98a2b3; }

/* Insights */
.dt-insight-cards { display: flex; flex-wrap: wrap; gap: 8px; margin: 6px 0 14px; }
.dt-insight-card { flex: 0 1 calc(20% - 8px); min-width: 110px; background: #f7f8fa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 9px 10px; text-align: center; }
.dt-insight-card.dt-insight-warn { background: #fdecea; border-color: #f3b6ad; }
.dt-insight-n { font-size: 19px; font-weight: 800; color: #061D47; }
.dt-insight-l { font-size: 10px; color: #6b7280; margin-top: 3px; line-height: 1.35; }
.dt-insight-table { width: 100%; border-collapse: collapse; margin-top: 6px; }
.dt-insight-table th, .dt-insight-table td { text-align: left; padding: 7px 8px; border-bottom: 1px solid #eef0f4; font-size: 13px; }
.dt-insight-table th { color: #6b7280; font-weight: 600; }

/* Settings: templates + attachments */
.dt-tpl-row { display: grid; grid-template-columns: 1fr 90px 1fr auto; gap: 6px; align-items: start; margin-bottom: 8px; }
.dt-tpl-row textarea { grid-column: 1 / -1; }
.dt-att-list { list-style: none; margin: 6px 0; padding: 0; }
.dt-att-item { display: flex; align-items: center; gap: 8px; padding: 5px 0; border-bottom: 1px solid #f3f4f7; }
.dt-att-name { font-size: 13px; color: #1c2536; }
.dt-upload-label { display: inline-block; cursor: pointer; }
.dt-tpl-pick { max-width: 220px; margin-bottom: 6px; }
/* Compose footer: paperclip (attach) on the left, Send on the right */
.dt-foot-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.dt-foot-spacer { flex: 1; }
.dt-attach-btn { display: inline-flex; align-items: center; gap: 3px; border: 1px solid #d6dbe4; background: #fff; border-radius: 7px; padding: 6px 9px; font-size: 15px; cursor: pointer; color: #1c2536; }
.dt-attach-btn:hover { border-color: #1b5fd0; }
.dt-attach-btn.active { border-color: #1b5fd0; background: #eef4ff; }
.dt-attach-count { font-size: 11px; font-weight: 700; color: #1b5fd0; }
.dt-modal.dt-modal-sm { width: 460px; max-width: 92%; }
.dt-att-pick-list { list-style: none; margin: 4px 0 0; padding: 0; }
.dt-att-pick-item { padding: 10px 4px; border-bottom: 1px solid #f3f4f7; }
.dt-att-pick-item:first-child { padding-top: 2px; }
.dt-att-pick-item label { display: flex; align-items: center; gap: 8px; font-size: 14px; line-height: 1.5; color: #1c2536; cursor: pointer; }
.dt-att-clip { font-size: 14px; }
.dt-att-name-input { flex: 1; }
.dt-rowdrag-cell { text-align: center; color: #c0c7d1; }
.dt-rowdrag-cell .dt-drag { cursor: grab; font-size: 13px; }
.dt-rowdrag-cell .dt-drag:hover { color: #6b7280; }
.dt-row.row-dragover { box-shadow: inset 0 3px 0 #1b5fd0 !important; }
.dt-bucket-sep > td { background: #f7f9fc; padding: 6px 14px; border-top: 1px solid #e6eaf0; border-bottom: 1px solid #e6eaf0; }
.dt-bucket-sep-label { font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #61708a; }
.dt-substeps-head { display: flex; align-items: center; justify-content: space-between; }
.dt-step-pct { font-size: 11px; font-weight: 700; color: #061D47; }
.dt-step-progress { height: 5px; background: #eef0f4; border-radius: 3px; overflow: hidden; margin: 6px 0 10px; }
.dt-step-progress-fill { display: block; height: 100%; background: #1b5fd0; border-radius: 3px; transition: width 0.15s; }
.dt-next-row { display: flex; align-items: center; justify-content: center; gap: 4px; }
.dt-step-arrow { border: none; background: none; color: #b0b8c4; cursor: pointer; font-size: 11px; padding: 2px 3px; border-radius: 4px; flex: 0 0 auto; }
.dt-step-arrow:hover:not(:disabled) { color: #1c2536; background: #eef1f5; }
.dt-step-arrow:disabled { opacity: 0.25; cursor: default; }
.dt-chip-done { color: #1d8a4a; margin-left: 4px; }
.dt-step-chip-colored { border-width: 1.5px; border-style: solid; background: #fff; font-weight: 700; }
.dt-send-row { display: flex; gap: 8px; align-items: center; margin: 6px 0; }
.dt-recipient { flex: 1; min-width: 0; }
.dt-recipient-pick { max-width: 180px; }
.dt-subject-row { display: flex; gap: 8px; align-items: center; margin: 6px 0; }
.dt-subject-tag { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #8a93a3; flex: 0 0 auto; }
.dt-subject-input { }
.dt-no-contacts { font-size: 12px; margin: 4px 0; }
.dt-avh { font-size: 12px; line-height: 1.5; white-space: nowrap; }
.dt-avh b { color: #061D47; display: inline-block; width: 14px; }
.dt-avh-overall { margin-top: 4px; padding-top: 4px; border-top: 1px solid #eef0f4; font-weight: 700; color: #061D47; }
.dt-overall-bar { display: block; width: 100%; max-width: 120px; height: 6px; background: #eef0f4; border-radius: 6px; overflow: hidden; margin-top: 3px; }
.dt-overall-bar .dt-bar-fill { display: block; height: 100%; }
/* breathing room between Next action and Latest note */
.dt-note-th, .dt-note-td { padding-left: 96px !important; }

/* guided task modal */
.dt-task-desc { font-size: 14px; color: #344; line-height: 1.5; margin: 0; }
.dt-pending-banner { display: flex; align-items: center; justify-content: space-between; gap: 12px; background: #fff5e6; border: 1px solid #f0d8a8; border-radius: 8px; padding: 10px 14px; font-size: 13px; color: #8a5d12; }
.dt-task-section { border-top: 1px solid #eef0f4; padding-top: 14px; }
.dt-comms-list { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; max-height: 220px; overflow-y: auto; }
.dt-comm { display: flex; gap: 9px; align-items: flex-start; padding: 8px 10px; border-radius: 8px; }
.dt-comm.dir-out { background: #eef4ff; }
.dt-comm.dir-in { background: #eefbf1; border: 1px solid #cdeed6; }
.dt-comm-icon { font-size: 14px; }
.dt-comm-body { flex: 1; min-width: 0; }
.dt-step-nav { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: -4px 0 4px; }
.dt-step-count { font-size: 12px; font-weight: 700; color: #6b7280; }
.dt-step-done-tag { color: #1d8a4a; margin-left: 8px; }
.dt-compose { background: #f7f8fa; border: 1px solid #e6e9ef; border-radius: 8px; padding: 12px; margin-bottom: 14px; display: flex; flex-direction: column; gap: 8px; }
.dt-compose-subject { width: 100%; }
.dt-compose-body { width: 100%; resize: vertical; min-height: 88px; }
.dt-comm-click { cursor: pointer; }
.dt-comm-click:hover { filter: brightness(0.98); }
.dt-comm-subj { font-size: 12px; font-weight: 600; color: #061D47; margin-left: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 280px; }
.dt-comm-preview { white-space: pre-wrap; font-size: 13px; color: #333; margin-top: 4px; padding: 6px 8px; background: #f6f8fb; border-radius: 6px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
/* AI Activity — two-row metrics strip */
.dt-ai-statrow { display: flex; flex-wrap: wrap; gap: 10px; align-items: stretch; }
.dt-ai-rowlabel { display: flex; align-items: center; min-width: 58px; font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; }
.dt-ai-stat { flex: 1; min-width: 110px; background: #f6f8fb; border-radius: 8px; padding: 8px 12px; }
.dt-ai-stat-l { font-size: 11px; color: #6b7280; }
.dt-ai-stat-n { font-size: 20px; font-weight: 700; color: #061D47; }
.dt-ai-stat-s { font-size: 11px; color: #6b7280; }
.dt-ai-stat-click { cursor: pointer; transition: box-shadow .12s; }
.dt-ai-stat-click:hover { box-shadow: inset 0 0 0 1px #cdd5e1; }
.dt-optout-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dt-optout-table th { text-align: left; color: #6b7280; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: .03em; padding: 6px 10px; border-bottom: 1px solid #eef1f6; }
.dt-optout-table td { padding: 8px 10px; border-bottom: 1px solid #f3f5f9; color: #1c2536; }
/* AI Activity — date tabs across the top */
.dt-ai-daytabs { display: flex; gap: 6px; overflow-x: auto; padding: 4px 0 10px; border-bottom: 1px solid #eef1f6; margin-bottom: 8px; }
.dt-ai-daytab { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px; background: #f6f8fb; border: 1px solid #e6e9ef; border-radius: 999px; cursor: pointer; padding: 6px 12px; font-weight: 600; color: #4a5365; font-size: 13px; white-space: nowrap; }
.dt-ai-daytab:hover { border-color: #19A99D; color: #061D47; }
.dt-ai-daytab.active { background: #061D47; border-color: #061D47; color: #fff; }
.dt-ai-daytab.active .dt-count { background: rgba(255,255,255,0.22); color: #fff; }
.dt-comms-list-flow { max-height: none; overflow: visible; }
.dt-open-badge { font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 10px; margin-left: 8px; }
.dt-open-badge.opened { background: #e6f7f4; color: #0f8b7e; }
.dt-open-badge.unopened { background: #eef1f6; color: #8a93a3; }
.dt-comms-list-tall { max-height: 340px; }
.dt-read-chip { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; padding: 1px 7px; border-radius: 10px; flex: 0 0 auto; }
.dt-read-chip.is-read { background: #cdeed6; color: #1d8a4a; }
.dt-read-chip.is-unread { background: #eceff3; color: #8a93a3; }
.dt-read-chip.dt-new-flag { background: #fdecea; color: #c0392b; }
.dt-modal.dt-modal-comm { width: 760px; max-width: 94%; max-height: 88vh; overflow-y: auto; }
.dt-comm-view-tags { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #6b7280; }
.dt-reply-box { margin-top: 14px; padding-top: 14px; border-top: 1px solid #eef0f4; display: flex; flex-direction: column; gap: 8px; }
.dt-comm-team { font-size: 11px; font-weight: 700; color: #1b5fd0; background: #eef4ff; border-radius: 8px; padding: 1px 7px; flex: 0 0 auto; }
.dt-comm.unreadrow { box-shadow: inset 3px 0 0 #e74c3c; }
.dt-comm-view-body { white-space: pre-wrap; font-size: 14px; line-height: 1.55; color: #1c2536; background: #f7f8fa; border-radius: 8px; padding: 16px; margin-top: 4px; max-height: 300px; overflow-y: auto; }
.dt-reader-nav { display: inline-flex; align-items: center; gap: 8px; margin-left: auto; font-size: 12px; }
.dt-msg-search { width: 100%; margin-bottom: 8px; }
.dt-comm-head { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.dt-dir-badge { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; padding: 1px 7px; border-radius: 10px; flex: 0 0 auto; }
.dt-dir-badge.dir-out { background: #d6e3fb; color: #1b5fd0; }
.dt-dir-badge.dir-in { background: #cdeed6; color: #1d8a4a; }
.dt-comm-subject-line { flex: 1; min-width: 0; font-size: 13px; font-weight: 700; color: #1c2536; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dt-comm-caret { color: #9aa3b2; font-size: 11px; flex: 0 0 auto; }
.dt-comm-text { font-size: 13px; color: #1c2536; white-space: pre-wrap; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(0,0,0,0.06); }
.dt-comm-meta { font-size: 11px; font-weight: 600; color: #6b7280; margin-top: 2px; }
.dt-channel-btns { display: flex; gap: 8px; margin: 8px 0; }
.dt-btn.active { background: #061D47; color: #fff; }
.dt-task-foot { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; border-top: 1px solid #eef0f4; padding-top: 14px; }
.dt-confirm { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; flex-wrap: wrap; }

.dt-sev-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex: 0 0 auto; }
.dt-sev-dot.sev-high { background: #e74c3c; }
.dt-sev-dot.sev-medium { background: #e67e22; }
.dt-sev-dot.sev-low { background: #3498db; }
.dt-sev-dot.sev-info { background: #b0b7c3; }

/* All teams table */
.dt-table-scroll { overflow-x: auto; }
.dt-table-wide { min-width: 0; }
.dt-table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
.dt-table thead th { text-align: left; padding: 8px 8px; border-bottom: 1px solid #eef0f4; vertical-align: bottom; }
.dt-table thead th.dt-next-cell, .dt-table thead th.dt-next-cell .dt-th { text-align: center; }
.dt-th { display: inline-block; color: #8a93a3; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; line-height: 1.2; }
.dt-table thead th.sortable { cursor: pointer; user-select: none; }
.dt-table thead th.sortable:hover .dt-th { color: #061D47; }
.dt-table thead th.active .dt-th { color: #061D47; }
.dt-yes { color: #1d8a4a; font-weight: 700; }
.dt-row:hover { background: #fbfcfd; }
.dt-table tbody td { padding: 8px 8px; border-bottom: 1px solid #f3f4f7; vertical-align: middle; overflow-wrap: break-word; }
.dt-clickable { cursor: pointer; }
.dt-team-name { font-weight: 700; color: #1c2536; display: flex; align-items: center; gap: 7px; min-width: 0; }
.dt-team-name-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.dt-team-name .dt-team-dot, .dt-team-name .dt-envelope { flex: 0 0 auto; }
.dt-team-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex: 0 0 auto; }
.dt-team-dot-lg { width: 14px; height: 14px; }
.dt-status-chip { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 12px; }
.st-prospect { background: #eceff3; color: #5b6472; }
.st-free { background: #e7f0ff; color: #1b5fd0; }
.st-paying { background: #def7e6; color: #1d8a4a; }
.dt-auto { font-size: 10px; color: #1d8a4a; font-weight: 700; }
.dt-manual { font-size: 11px; color: #9aa3b2; font-weight: 600; }
.dt-muted { color: #9aa3b2; }
.dt-pct { font-weight: 700; color: #061D47; font-size: 13px; }
.dt-due-red { color: #c0392b; font-weight: 700; }
.dt-envelope { background: #e74c3c; color: #fff; font-size: 11px; font-weight: 700; border-radius: 10px; padding: 1px 7px; margin-left: 6px; white-space: nowrap; }
/* Done-today: deliberate touch made today — the row recedes so the eye lands on what's still pending. */
.dt-done-chip { display: inline-block; margin-top: 6px; background: #e7f6ec; color: #1e7e45; font-size: 11px; font-weight: 700; border-radius: 10px; padding: 2px 9px; white-space: nowrap; }
.dt-progress-chip { display: inline-block; margin-top: 6px; background: #e6edfb; color: #2c4a9e; font-size: 11px; font-weight: 700; border-radius: 10px; padding: 2px 9px; white-space: nowrap; }
.dt-chip-btn { border: none; cursor: pointer; font-family: inherit; line-height: 1.4; }
.dt-chip-btn:hover { filter: brightness(0.96); }
.dt-input-err { font-size: 12px; color: #c0392b; margin-top: -2px; }
.dt-row.dt-done-today > td:not(.dt-rowdrag-cell) { opacity: 0.5; transition: opacity 0.12s; }
.dt-row.dt-done-today:hover > td:not(.dt-rowdrag-cell) { opacity: 1; }
.dt-new-chip { background: #fdecea; color: #c0392b; font-size: 12px; font-weight: 700; border-radius: 14px; padding: 5px 12px; align-self: center; }
.dt-fu-cell-title { font-size: 13px; font-weight: 600; color: #1c2536; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dt-fu-cell-date { font-size: 12px; color: #9aa3b2; margin-top: 1px; }
.dt-fu-tag { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #b9770e; background: #fff2dd; border-radius: 10px; padding: 1px 7px; }
.dt-note-cell { max-width: 220px; }
.dt-note-text { font-size: 12px; color: #444; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dt-bar { background: #eef0f4; border-radius: 6px; height: 8px; position: relative; overflow: hidden; margin-top: 4px; }
.dt-bar-thin { height: 6px; max-width: 90px; }
.dt-bar-fill { height: 100%; border-radius: 6px; }
.dt-bar-fill.overall { background: #061D47; }
.dt-bar-fill.agents { background: #1b5fd0; }
.dt-bar-fill.vendors { background: #b9770e; }
.dt-bar-fill.homeowners { background: #6b34c9; }
.dt-bar-marked { overflow: visible; }
.dt-marker { position: absolute; top: -2px; width: 2px; height: 12px; background: #1d8a4a; }

.dt-row-end { display: flex; align-items: center; gap: 8px; justify-content: flex-end; }
.dt-row-delete { border: none; background: transparent; color: #b0b7c3; font-size: 14px; line-height: 1; cursor: pointer; padding: 4px 6px; border-radius: 6px; }
.dt-row-delete:hover { color: #c0392b; background: #fdecea; }

.dt-empty { text-align: center; color: #9aa3b2; padding: 20px; font-size: 14px; }
.dt-empty-sm { padding: 8px 0; }

/* ===== Profile (full page) ===== */
.dt-profile { max-width: 940px; margin: 0 auto; }
.dt-profile-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.dt-back { background: none; border: none; color: #1b5fd0; font-size: 14px; font-weight: 600; cursor: pointer; padding: 6px 0; }
.dt-back:hover { text-decoration: underline; }
.dt-profile-actions { display: flex; gap: 12px; align-items: center; }
.dt-save-state { font-size: 12px; font-weight: 600; color: #9aa3b2; }
.dt-save-state.saving { color: #b9770e; }
.dt-save-state.saved { color: #1d8a4a; }
.dt-profile-title { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
.dt-name-input { font-size: 24px !important; font-weight: 700 !important; color: #061D47 !important; border: none; border-bottom: 1px dashed #cbd2dd; padding: 2px 0; min-width: 240px; transform: none !important; }
.dt-name-input:focus { outline: none; border-bottom-color: #1b5fd0; }
.dt-profile-body { display: flex; flex-direction: column; }

.dt-card-title { font-size: 15px; font-weight: 700; color: #061D47; margin: 0 0 18px; padding-bottom: 12px; border-bottom: 1px solid #eef0f4; display: flex; align-items: center; gap: 8px; }

.dt-funnel { display: flex; flex-direction: column; gap: 18px; }
.dt-leg-head { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 2px; }
.dt-leg-head b { color: #061D47; }

.dt-alert { border-left: 4px solid #ccc; border-radius: 8px; padding: 12px 14px; margin-bottom: 10px; background: #fafbfc; }
.dt-alert:last-child { margin-bottom: 0; }
.dt-alert.sev-high { border-left-color: #e74c3c; }
.dt-alert.sev-medium { border-left-color: #e67e22; }
.dt-alert.sev-low { border-left-color: #3498db; }
.dt-alert.sev-info { border-left-color: #b0b7c3; }
.dt-alert-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.dt-alert-main { flex: 1; }
.dt-alert-title { font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.dt-alert-detail { font-size: 12px; color: #6b7280; margin-top: 4px; }
.dt-script { font-size: 12px; color: #345; background: #eef3fb; border-radius: 6px; padding: 8px 10px; margin-top: 10px; }

.dt-link-row { display: flex; gap: 8px; margin-bottom: 8px; }
/* Field captions use .dt-flabel (a span) to dodge the global label{} !important rule */
.dt-input, .dt-field input, .dt-field select, .dt-field textarea {
  width: 100%; border: 1px solid #d6dbe4; border-radius: 7px; padding: 9px 11px; font-size: 14px; box-sizing: border-box; font-family: inherit; color: #1c2536; transform: none;
}
.dt-input:focus, .dt-field input:focus, .dt-field select:focus, .dt-field textarea:focus { outline: none; border-color: #1b5fd0; }
.dt-tenant-results { list-style: none; margin: 0 0 8px; padding: 0; border: 1px solid #e6e9ef; border-radius: 7px; max-height: 180px; overflow-y: auto; }
.dt-tenant-results li { padding: 9px 11px; font-size: 13px; cursor: pointer; }
.dt-tenant-results li:hover { background: #f1f4f9; }
.dt-linked { font-size: 12px; color: #444; margin-bottom: 14px; }
.dt-linked code { background: #eef0f4; padding: 1px 5px; border-radius: 4px; }
.dt-link-clear { background: none; border: none; color: #c0392b; font-size: 12px; cursor: pointer; margin-left: 6px; }

.dt-fields-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 22px; align-items: start; }
@media (max-width: 640px) { .dt-fields-grid { grid-template-columns: 1fr; } }
.dt-field { display: flex; flex-direction: column; }
.dt-flabel { font-size: 12px !important; color: #6b7280 !important; font-weight: 600 !important; margin-bottom: 6px; line-height: 1.3 !important; transform: none !important; max-width: 100% !important; display: block; text-transform: none; }
.dt-field-check { justify-content: flex-end; }
.dt-check-label { display: flex !important; flex-direction: row !important; align-items: center; gap: 8px; color: #1c2536 !important; font-size: 14px !important; font-weight: 600 !important; transform: none !important; margin: 0 !important; }
.dt-check-label input { width: auto; }
.dt-span2 { grid-column: 1 / -1; }

.dt-add-update { display: flex; gap: 8px; margin-bottom: 14px; align-items: center; }
.dt-updates { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.dt-update { border-left: 3px solid #e6e9ef; padding-left: 12px; }
.dt-update-meta { display: flex; gap: 8px; align-items: baseline; font-size: 12px; }
.dt-update-text { font-size: 13px; margin-top: 2px; }
.dt-link-btn { background: none; border: none; color: #1b5fd0; font-size: 12px; cursor: pointer; padding: 0; }
.dt-link-danger { color: #c0392b; margin-left: 8px; }

.dt-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1000; display: flex; align-items: center; justify-content: center; }
.dt-overlay-top { z-index: 1100; }
.dt-modal { position: relative; background: #fff; border-radius: 12px; padding: 24px; width: 400px; max-width: 92%; display: flex; flex-direction: column; gap: 16px; max-height: 90vh; overflow-y: auto; }
.dt-modal-x { position: absolute; top: 14px; right: 16px; border: none; background: none; font-size: 18px; color: #9aa3b2; cursor: pointer; line-height: 1; padding: 4px 6px; border-radius: 6px; z-index: 2; }
.dt-modal-x:hover { color: #1c2536; background: #f1f4f9; }
.dt-modal.dt-modal-wide { width: 680px; }
.dt-modal-profile { width: 780px; max-width: 94%; max-height: 92vh; overflow-y: auto; padding: 22px 26px; }
.dt-modal-profile .dt-card { margin-bottom: 14px; }
.dt-copy { border: none; background: #eef1f6; color: #1b5fd0; font-size: 11px; font-weight: 700; border-radius: 5px; padding: 1px 8px; cursor: pointer; margin-left: 8px; }
.dt-copy:hover { background: #e2e7ef; }
.dt-foot-right { display: flex; gap: 10px; justify-content: flex-end; }

/* Daily tasks */
.dt-daily-btn { position: relative; }
.dt-daily-badge { background: #e74c3c; color: #fff; font-size: 11px; font-weight: 700; border-radius: 12px; padding: 0 7px; margin-left: 6px; }
.dt-daily-list { list-style: none; margin: 0 0 6px; padding: 0; display: flex; flex-direction: column; gap: 6px; max-height: 62vh; overflow-y: auto; }
.dt-daily-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border: 1px solid #eceef2; border-left-width: 4px; border-left-color: #cbd2dd; border-radius: 8px; }
.dt-daily-item.today { border-left-color: #e67e22; }
.dt-daily-item.overdue { border-left-color: #e74c3c; background: #fdf3f2; }
.dt-daily-item input { margin-top: 3px; }
.dt-daily-main { flex: 1; }
.dt-daily-title { font-size: 14px; font-weight: 600; }
.dt-daily-sub { font-size: 12px; color: #6b7280; margin-top: 2px; display: flex; align-items: center; gap: 6px; }
.dt-daily-due { color: #6b7280; }
.dt-daily-item.overdue .dt-daily-due { color: #c0392b; font-weight: 700; }
.dt-link { color: #1b5fd0; cursor: pointer; }
.dt-link:hover { text-decoration: underline; }

/* Follow-up prompt + team tasks in the team modal */
.dt-followup-prompt { background: #f4f8ff; border: 1px solid #d6e3fb; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.dt-fu-date { max-width: 160px; }
.dt-team-tasks { list-style: none; margin: 0 0 12px; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.dt-team-task { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 6px 10px; background: #f7f8fa; border-radius: 7px; }
.dt-team-task.overdue { background: #fdf3f2; }
.dt-team-task-title { font-weight: 600; }
.dt-fu-edit-title { flex: 1; min-width: 0; }
.dt-fu-edit-date { max-width: 150px; flex: 0 0 auto; }

.dt-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 12px 20px; border-radius: 8px; color: #fff; font-size: 14px; font-weight: 600; z-index: 1100; box-shadow: 0 8px 24px rgba(0,0,0,0.25); }
.dt-toast.ok { background: #1d8a4a; }
.dt-toast.err { background: #c0392b; }
</style>
